import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../../navigation/navigation.types";
import type { ApolloCache } from "@apollo/client";
import { useDebounce } from "use-debounce";
import {
  EditingTeamRole,
  ExternalVisibility,
  ExternalAssignmentSubmissionKind,
  GetCourseVersionHistoryDocument,
  GetModernizedCourseDocument,
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  ModernizedDescriptionFormat,
  ModernizedCourseExportFormat,
  type CreateIleInput,
  type CreateModernizedPageActivityInput,
  type CreateModernizedProjectActivityInput,
  type GetModernizedCoursePublishHistoryQuery,
  type IleObjectiveInput,
  type ObjectiveInput,
  type UpdateAiTutorInput,
  type VideoDataInput,
  type UpdateModernizedActivityFieldsMutationVariables,
  useCreateIleForModernizedRecommendationMutation,
  useCreateModernizedAssignmentActivityMutation,
  useCreateModernizedIleActivityMutation,
  useCreateModernizedModuleMutation,
  useDeleteModernizedModuleMutation,
  useDeleteModernizedAssignmentActivityMutation,
  useDeleteModernizedIleActivityMutation,
  useDeleteModernizedPageActivityMutation,
  useDeleteModernizedProjectActivityMutation,
  useCreateModernizedPageActivityMutation,
  useCreateModernizedProjectActivityMutation,
  useGenerateIleRecommendationContentMutation,
  useGetModernizedCourseEditingTeamQuery,
  useGetModernizedCoursePublishHistoryLazyQuery,
  useGetUsersWithElevatedRolesQuery,
  usePublishModernizedActivitiesMutation,
  useRemoveModernizedCourseEditingTeamMemberMutation,
  useRequestModernizedCourseExportMutation,
  useSetModernizedCourseEditingTeamMemberMutation,
  useUndoDeleteModernizedActivityMutation,
  useUndoDeleteModernizedModuleMutation,
  useMoveModernizedModuleActivityMutation,
  useReorderModernizedModuleActivitiesMutation,
  useReorderModernizedModulesMutation,
  useUpdateAiTutorMutation,
  useUpdateModernizedActivityFieldsMutation,
} from "@axio/graphql/generated/graphql";

import CustomAdminModal from "../../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { useAppConfig } from "../../../../AppConfigProvider";
import { hasMinRole, UserRoleUI } from "../../../../constants/rolesAndPermissions";
import { usePreviewMode } from "../../../../contexts/PreviewModeContext";
import { useToast } from "../../../../contexts/Toast/ToastProvider";
import { useAuth } from "../../../../hooks/useAuth";

import ReviewPanel from "./ReviewPanel";
import ModernizerAddActivityDrawer from "./components/ModernizerAddActivityDrawer";
import type { ModernizerCreateActivityInput } from "./components/ModernizerAddActivityTypes";
import ModernizerAddModuleModal, { type ModernizerAddModuleInput } from "./components/ModernizerAddModuleModal";
import ModernizerAddAssignmentSheet, {
  type ModernizerAddAssignmentInput,
} from "./components/ModernizerAddAssignmentSheet";
import ModernizerIleCreationSheet from "./components/ModernizerIleCreationSheet";
import type { TeamManagementUserOption } from "./components/TeamManagementPanel";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "./constants/itemTypeLabels";
import type { ActivityDataInput } from "../../../../components/customCoursework/ActivityForms/InteractiveLearningForm/InteractiveLearningFormCore";
import { useHistoricalSnapshotReview } from "./hooks/useHistoricalSnapshotReview";
import type {
  CourseEvaluatorReviewActivityMove,
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFieldEdits,
} from "./types/reviewTypes";
import {
  buildTriageQueryState,
  parseTriageQueryState,
  type TriageDimension,
  type TriageQueryState,
  type TriageSurface,
} from "./triageQueryParams";
import { buildCourseModernizerReviewPermissions } from "./reviewPermissions";
import { getManagedFileDownloadAction, submitAuthenticatedHiddenDownloadForm } from "./utils/webFileDownload";

interface DecisionEntry {
  decision: CourseEvaluatorReviewDecision;
}

function getSidebarItemType(item: CourseEvaluatorReviewSidebarItem): CourseEvaluatorReviewItemType | undefined {
  const itemTypeMeta = item.meta?.find(
    (meta): meta is { kind: "itemType"; itemType: CourseEvaluatorReviewItemType } => meta.kind === "itemType"
  );
  return itemTypeMeta?.itemType;
}

function getSidebarItemIsNew(item: CourseEvaluatorReviewSidebarItem): boolean {
  return Boolean(item.meta?.some((meta) => meta.kind === "tag" && meta.tag === "new"));
}

function augmentManualSidebarItem(
  item: CourseEvaluatorReviewSidebarItem,
  detail: ActivityDetail | undefined
): CourseEvaluatorReviewSidebarItem {
  if (!detail?.isManuallyAuthored) {
    return item;
  }

  const manualItemType =
    item.entityType === ModernizedActivityEntityType.Project
      ? "project"
      : item.entityType === ModernizedActivityEntityType.Assignment
        ? "assignment"
        : undefined;

  const existingMeta = item.meta ?? [];
  const nextMeta = existingMeta.filter((meta) => {
    if (meta.kind === "manual") {
      return false;
    }

    if (
      meta.kind === "itemType" &&
      (meta.itemType === "assignment" || meta.itemType === "project" || manualItemType !== undefined)
    ) {
      return false;
    }

    return true;
  });

  if (manualItemType) {
    nextMeta.unshift({ kind: "itemType", itemType: manualItemType });
  }

  nextMeta.push({ kind: "manual" });

  return {
    ...item,
    meta: nextMeta,
  };
}

function getItemTypeLabel(itemType: CourseEvaluatorReviewItemType): string {
  if (itemType === "file") {
    return "File";
  }

  if (itemType === "link") {
    return "External URL";
  }

  return COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType];
}

function isSupportedActivityDeleteItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  if (item.entityType === ModernizedActivityEntityType.IleRecommendation) {
    return getSidebarItemIsNew(item);
  }

  return (
    item.entityType === ModernizedActivityEntityType.Assignment ||
    item.entityType === ModernizedActivityEntityType.Project ||
    item.entityType === ModernizedActivityEntityType.OtherActivity
  );
}

function getActivityDeleteLabel(item: CourseEvaluatorReviewSidebarItem): string {
  const itemType = getSidebarItemType(item);
  if (item.entityType === ModernizedActivityEntityType.Project) {
    return "project";
  }
  if (item.entityType === ModernizedActivityEntityType.Assignment) {
    return itemType ? getItemTypeLabel(itemType).toLowerCase() : "assignment";
  }
  if (item.entityType === ModernizedActivityEntityType.OtherActivity) {
    return itemType ? getItemTypeLabel(itemType).toLowerCase() : "activity";
  }
  if (item.entityType === ModernizedActivityEntityType.IleRecommendation) {
    return "ILE";
  }
  return "assignment";
}

function sentenceCaseActivityLabel(label: string): string {
  return label === "ILE" ? "ILE" : label.charAt(0).toUpperCase() + label.slice(1);
}

function isTerminalDeletedItem(
  item: CourseEvaluatorReviewSidebarItem,
  decision: CourseEvaluatorReviewDecision
): boolean {
  return item.pendingDelete === true && (item.status === ModernizedActivityStatus.Published || decision === "synced");
}

/** Map sidebar item kind to the category label shown in the publish sheet. */
function getCategoryLabel(item: CourseEvaluatorReviewSidebarItem): string {
  const itemType = getSidebarItemType(item);

  if (item.pendingDelete === true) {
    if (item.entityType === ModernizedActivityEntityType.Module) {
      return "Module deleted";
    }
    return `${sentenceCaseActivityLabel(getActivityDeleteLabel(item))} deleted`;
  }

  if (item.entityType === ModernizedActivityEntityType.Assessment || itemType === "quiz") {
    return "Quiz";
  }

  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      if (itemType === "checklist" || itemType === "file" || itemType === "link") {
        return getItemTypeLabel(itemType);
      }
      return "Assignment";
    case ModernizedActivityEntityType.Module:
      return "Module Description";
    case ModernizedActivityEntityType.ContentSection:
      return "Course Content";
    case ModernizedActivityEntityType.Project:
      return "Project";
    case ModernizedActivityEntityType.OtherActivity:
      if (itemType === "file") {
        return "File Topic";
      }
      if (itemType) {
        return getItemTypeLabel(itemType);
      }
      return "Activity";
    case ModernizedActivityEntityType.IleRecommendation:
      return "Axio ILE";
    case ModernizedActivityEntityType.Assessment:
      return "Quiz";
  }

  switch (item.kind) {
    case "moduleDescription":
      return "Module Description";
    case "recommendation":
      return "Recommendation";
    case "assignment":
      return "Assignment";
    case "suggestedContent":
      return "Suggested Content";
    default:
      return "Change";
  }
}

interface UndoEntry {
  itemId: string;
  previous: DecisionEntry;
}

interface PendingActivityDeleteState {
  item: CourseEvaluatorReviewSidebarItem;
  activityLabel: string;
}

type ActivityDeletePayload = {
  success: boolean;
  message?: string | null;
  activityId?: string | null;
  deletedActivityId?: string | null;
  wasDiscarded: boolean;
  pendingDelete: boolean;
};

type GeneratedIlePrefill = {
  description: string;
  objectives: Array<{
    objective: string;
    sequence?: number | null;
    evalCriteria: string[];
  }>;
};

interface PendingModuleDeleteState {
  group: CourseEvaluatorReviewSidebarGroup;
  moduleItem: CourseEvaluatorReviewSidebarItem;
  activeActivityCount: number;
  stagedActivityCount: number;
  mode: "blocked" | "confirm";
}

interface CourseEvaluatorReviewContainerProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  onExitReview: () => void;
  initialQueryState?: TriageQueryState;
  onRefreshReview: () => Promise<unknown>;
}

type ModernizedActivityCacheTypename =
  | "ModernizedAssignment"
  | "ModernizedModule"
  | "ModernizedOtherActivity"
  | "ModernizedCourseContentSection"
  | "ModernizedAssessment"
  | "ModernizedProject"
  | "ILERecommendation";

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];
type PublishHistoryItem = PublishHistoryRecord["autoPublishedItems"][number];
type PublishHistoryManualStep = PublishHistoryRecord["manualSteps"][number];
type EditingTeamMutationOperation =
  | { kind: "remove"; userId: string }
  | { kind: "set"; userId: string; role: EditingTeamRole };

function mapPublishHistoryItem(item: PublishHistoryItem): PublishedPublishItem {
  return {
    id: item.id,
    title: item.title,
    category: item.category,
    moduleName: item.moduleName ?? "Course-Level Content",
  };
}

function mapPublishHistoryManualStep(step: PublishHistoryManualStep): PublishManualStep {
  return {
    id: step.id,
    title: step.title,
    category: step.category,
    moduleName: step.moduleName,
    action: step.action,
    detail: step.detail,
    externalUrl: step.externalUrl,
    downloadUrl: step.downloadUrl,
  };
}

function getActivityCacheTypename(entityType: ModernizedActivityEntityType): ModernizedActivityCacheTypename {
  switch (entityType) {
    case ModernizedActivityEntityType.Assignment:
      return "ModernizedAssignment";
    case ModernizedActivityEntityType.Module:
      return "ModernizedModule";
    case ModernizedActivityEntityType.OtherActivity:
      return "ModernizedOtherActivity";
    case ModernizedActivityEntityType.ContentSection:
      return "ModernizedCourseContentSection";
    case ModernizedActivityEntityType.Assessment:
      return "ModernizedAssessment";
    case ModernizedActivityEntityType.Project:
      return "ModernizedProject";
    case ModernizedActivityEntityType.IleRecommendation:
      return "ILERecommendation";
  }
}

function updateActivityStatusInCache<TSerialized>(
  cache: ApolloCache<TSerialized>,
  item: CourseEvaluatorReviewSidebarItem,
  status: ModernizedActivityStatus
): void {
  const cacheId = cache.identify({
    __typename: getActivityCacheTypename(item.entityType),
    id: item.id,
  });

  if (!cacheId) {
    return;
  }

  cache.modify({
    id: cacheId,
    fields: {
      status: () => status,
      ...(item.pendingDelete === true
        ? {
            pendingDelete: () => true,
            externalUrl: () => null,
          }
        : {}),
    },
  });
}

function getDecisionFromStatus(status: ModernizedActivityStatus | null | undefined): CourseEvaluatorReviewDecision {
  switch (status) {
    case ModernizedActivityStatus.Queued:
      return "accepted";
    case ModernizedActivityStatus.Skipped:
      return "rejected";
    case ModernizedActivityStatus.Published:
      return "synced";
    default:
      return null;
  }
}

function getStatusFromDecision(decision: CourseEvaluatorReviewDecision): ModernizedActivityStatus {
  switch (decision) {
    case "accepted":
    case "edited":
      return ModernizedActivityStatus.Queued;
    case "rejected":
      return ModernizedActivityStatus.Skipped;
    case "synced":
      return ModernizedActivityStatus.Published;
    default:
      return ModernizedActivityStatus.Undecided;
  }
}

function buildInitialDecisions(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): Map<string, DecisionEntry> {
  const initialDecisions = new Map<string, DecisionEntry>();

  sidebarGroups.forEach((group) => {
    group.items.forEach((item) => {
      const decision = getDecisionFromStatus(item.status);
      if (decision) {
        initialDecisions.set(item.id, { decision });
      }
    });
  });

  return initialDecisions;
}

function getDecisionSourceSignature(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): string {
  return sidebarGroups
    .map((group) => `${group.id}:${group.items.map((item) => `${item.id}:${item.status ?? ""}`).join(",")}`)
    .join("|");
}

/**
 * Converts a user-entered date string to an ISO 8601 string for the GraphQL
 * DateTime scalar, preserving a tri-state:
 * - `undefined` → field not present/shown → leave untouched
 * - `null`       → user explicitly cleared → send null to backend
 * - `string`     → user set a date → parse and return ISO string (or null if unparseable)
 *
 * Uses UTC-based parsing to avoid timezone-dependent date shifts.
 */
function parseDateToIso(value: string | null | undefined): string | null | undefined {
  if (value === undefined) return undefined;
  if (!value.trim()) return null;
  // Parse as a UTC midnight date to avoid local-timezone shifts on date-only strings.
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const [, month, day, year] = match;
    const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  // Fall back to native parsing for ISO strings already in the correct format.
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function getEditMutationVariables(
  item: CourseEvaluatorReviewSidebarItem,
  editedFields: StructuredFieldEdits
): UpdateModernizedActivityFieldsMutationVariables | null {
  const itemType = getSidebarItemType(item);
  const recommendedDescriptionFormat =
    editedFields.description === undefined
      ? undefined
      : itemType === "page" || itemType === "webpage"
        ? ModernizedDescriptionFormat.Html
        : ModernizedDescriptionFormat.Markdown;

  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedPointsPossible: editedFields.points,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.Module:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        // Modules do not support due dates; omit recommendedDueDate entirely.
      };
    case ModernizedActivityEntityType.OtherActivity:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.Project:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.ContentSection:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedValue: editedFields.description,
      };
    case ModernizedActivityEntityType.Assessment:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedPointsPossible: editedFields.points,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.IleRecommendation:
      return null;
  }

  return null;
}

function isIleRecommendationItem(item: CourseEvaluatorReviewSidebarItem | null): boolean {
  return item?.entityType === ModernizedActivityEntityType.IleRecommendation;
}

function isExistingImportedIleItem(
  item: CourseEvaluatorReviewSidebarItem | null,
  detail: ActivityDetail | null | undefined
): boolean {
  return (
    isIleRecommendationItem(item) &&
    item !== null &&
    !getSidebarItemIsNew(item) &&
    Boolean(detail?.createdIleId) &&
    detail?.createdIleWasUserCreated !== true
  );
}

function getModuleDescriptionItem(
  group: CourseEvaluatorReviewSidebarGroup
): CourseEvaluatorReviewSidebarItem | undefined {
  return group.items.find((item) => item.entityType === ModernizedActivityEntityType.Module);
}

function getModuleDescriptionItemOrFallback(
  group: CourseEvaluatorReviewSidebarGroup
): CourseEvaluatorReviewSidebarItem {
  return (
    getModuleDescriptionItem(group) ?? {
      id: group.id,
      title: group.title,
      kind: "moduleDescription",
      entityType: ModernizedActivityEntityType.Module,
    }
  );
}

function countActiveModuleActivities(
  group: CourseEvaluatorReviewSidebarGroup,
  decisions: Map<string, DecisionEntry>
): number {
  return group.items.filter(
    (item) =>
      item.entityType !== ModernizedActivityEntityType.Module &&
      item.pendingDelete !== true &&
      decisions.get(item.id)?.decision !== "rejected"
  ).length;
}

function countModuleActivities(group: CourseEvaluatorReviewSidebarGroup): number {
  return group.items.filter((item) => item.entityType !== ModernizedActivityEntityType.Module).length;
}

function getPendingDeleteModuleForItem(
  item: CourseEvaluatorReviewSidebarItem,
  groups: CourseEvaluatorReviewSidebarGroup[]
): CourseEvaluatorReviewSidebarItem | null {
  if (item.entityType === ModernizedActivityEntityType.Module) {
    return null;
  }

  const group = groups.find((candidate) => candidate.items.some((groupItem) => groupItem.id === item.id));
  if (!group) {
    return null;
  }

  const moduleItem = getModuleDescriptionItem(group);
  return moduleItem?.pendingDelete === true ? moduleItem : null;
}

function mapObjectives(objectives: ActivityDataInput["objectives"] = []): IleObjectiveInput[] {
  return objectives.map((objective, index) => ({
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));
}

function mapObjectivesToUpdateInput(objectives: ActivityDataInput["objectives"] = []): ObjectiveInput[] {
  return objectives.map((objective, index) => ({
    id: objective.id,
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));
}

function mapVideoPool(videoPool: ActivityDataInput["videoPool"] = []): VideoDataInput[] {
  return videoPool.map((video) => ({
    videoId: video.videoId,
    objectiveText: video.objectiveText ?? "",
    objectiveSequence: video.objectiveSequence ?? 1,
    isTeacherAdded: Boolean(video.isTeacherAdded),
  }));
}

function normalizeDueDate(raw: string | Date | null | undefined): string | null {
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T12:00:00.000Z`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getMissingIleBulkWarningMessage(count: number): string {
  return count === 1
    ? "Create the ILE before adding this recommendation to the queue."
    : `Create ILEs before adding these ${count} recommendations to the queue.`;
}

const ILE_TARGET_AUDIENCE_OPTIONS = new Set([
  "Elementary School",
  "Middle School",
  "High School",
  "Undergraduate",
  "Graduate",
]);

function mapCourseTargetAudienceToIle(targetAudience: string | null | undefined): string | undefined {
  if (!targetAudience) {
    return undefined;
  }

  if (ILE_TARGET_AUDIENCE_OPTIONS.has(targetAudience)) {
    return targetAudience;
  }

  switch (targetAudience) {
    case "TRADITIONAL_STUDENT":
    case "Traditional student":
      return "Undergraduate";
    case "WORKING_PROFESSIONAL":
    case "Working professional":
      return "Graduate";
    case "GRADUATE_STUDENT":
    case "Graduate student":
      return "Graduate";
    case "MIXED":
    case "Mixed":
      return "Undergraduate";
    default:
      return undefined;
  }
}

function mapActivityDataToCreateIleInput(activityData: ActivityDataInput): CreateIleInput {
  const dueDate = normalizeDueDate(activityData.dueDate);
  return {
    title: activityData.title ?? "Untitled ILE",
    description: activityData.description ?? "",
    context: activityData.context ?? "",
    objectives: mapObjectives(activityData.objectives ?? activityData.rawObjectives),
    videoPool: mapVideoPool(activityData.videoPool),
    ...(dueDate ? { dueDate } : {}),
  };
}

function mapActivityDataToUpdateIleInput(activityData: ActivityDataInput): UpdateAiTutorInput {
  const dueDate = normalizeDueDate(activityData.dueDate);
  const updateInput: UpdateAiTutorInput = {
    title: activityData.title ?? "Untitled ILE",
    description: activityData.description ?? "",
    context: activityData.context ?? "",
    targetAudience: activityData.targetAudience ?? "",
    objectives: mapObjectivesToUpdateInput(activityData.objectives ?? activityData.rawObjectives),
    dueDate,
    activityState: activityData.activityState,
    scheduledDate: activityData.scheduledDate,
    visualizationId: activityData.visualizationId ?? null,
  };

  if (activityData.videoPool) {
    updateInput.videoPool = mapVideoPool(activityData.videoPool);
  }

  if (activityData.imagePool) {
    updateInput.imagePool = activityData.imagePool.map((image) => ({
      id: image.id,
      url: image.url,
      objectiveText: image.objectiveText,
      objectiveSequence: image.objectiveSequence,
      isTeacherUploaded: Boolean(image.isTeacherUploaded),
    }));
  }

  return updateInput;
}

function getActiveSurfaceForTriage(itemId: string | null, dimension: TriageDimension | null): TriageSurface {
  if (itemId) {
    return "entity";
  }

  if (dimension) {
    return "dimension";
  }

  return "overview";
}

function CourseEvaluatorReviewContainer({
  review,
  activityDetails,
  onExitReview,
  initialQueryState,
  onRefreshReview,
}: CourseEvaluatorReviewContainerProps) {
  const { showToast } = useToast();
  const { isCompanionMode = false } = useAppConfig() ?? {};
  const { enterPreviewMode } = usePreviewMode();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { userInfo } = useAuth();
  const [publishModernizedActivitiesMutation] = usePublishModernizedActivitiesMutation();
  const [requestModernizedCourseExportMutation] = useRequestModernizedCourseExportMutation();
  const [updateModernizedActivityFieldsMutation] = useUpdateModernizedActivityFieldsMutation();
  const [setEditingTeamMemberMutation, { loading: isSettingEditingTeamMember }] =
    useSetModernizedCourseEditingTeamMemberMutation();
  const [removeEditingTeamMemberMutation, { loading: isRemovingEditingTeamMember }] =
    useRemoveModernizedCourseEditingTeamMemberMutation();
  const [createIleForModernizedRecommendationMutation, { loading: isCreatingIle }] =
    useCreateIleForModernizedRecommendationMutation();
  const [generateIleRecommendationContentMutation, { loading: isGeneratingIleRecommendationContent }] =
    useGenerateIleRecommendationContentMutation();
  const [createModernizedAssignmentActivityMutation, { loading: isCreatingModernizedAssignment }] =
    useCreateModernizedAssignmentActivityMutation();
  const [deleteModernizedAssignmentActivityMutation] = useDeleteModernizedAssignmentActivityMutation();
  const [deleteModernizedPageActivityMutation] = useDeleteModernizedPageActivityMutation();
  const [deleteModernizedIleActivityMutation] = useDeleteModernizedIleActivityMutation();
  const [deleteModernizedProjectActivityMutation] = useDeleteModernizedProjectActivityMutation();
  const [deleteModernizedModuleMutation, { loading: isDeletingModernizedModule }] = useDeleteModernizedModuleMutation();
  const [undoDeleteModernizedActivityMutation] = useUndoDeleteModernizedActivityMutation();
  const [undoDeleteModernizedModuleMutation] = useUndoDeleteModernizedModuleMutation();
  const [createModernizedModuleMutation, { loading: isCreatingModernizedModule }] = useCreateModernizedModuleMutation();
  const [reorderModernizedModulesMutation] = useReorderModernizedModulesMutation();
  const [reorderModernizedModuleActivitiesMutation] = useReorderModernizedModuleActivitiesMutation();
  const [moveModernizedModuleActivityMutation] = useMoveModernizedModuleActivityMutation();
  const [createModernizedPageActivityMutation, { loading: isCreatingModernizedPageActivity }] =
    useCreateModernizedPageActivityMutation();
  const [createModernizedProjectActivityMutation, { loading: isCreatingModernizedProjectActivity }] =
    useCreateModernizedProjectActivityMutation();
  const [createModernizedIleActivityMutation, { loading: isCreatingModernizedIleActivity }] =
    useCreateModernizedIleActivityMutation();
  const [updateAiTutorMutation, { loading: isUpdatingIle }] = useUpdateAiTutorMutation();
  const [fetchPublishHistory] = useGetModernizedCoursePublishHistoryLazyQuery({ fetchPolicy: "network-only" });
  const initialSelectedItemIdCandidate = initialQueryState?.sheet === "open" ? initialQueryState.entityId : null;
  const validInitialItemIds = new Set(review.sidebarGroups.flatMap((group) => group.items.map((item) => item.id)));
  const initialSelectedItemId =
    initialSelectedItemIdCandidate && validInitialItemIds.has(initialSelectedItemIdCandidate)
      ? initialSelectedItemIdCandidate
      : null;
  const [selectedItemId, setSelectedItemId] = useState<string | null>(initialSelectedItemId);
  // Dimension-driven navigation is not fully interactive in this container yet, so
  // only hydrate entity-driven selection from the URL. This avoids landing in a
  // dimension-focused state that the user cannot subsequently change or clear here.
  const [selectedDimension, setSelectedDimension] = useState<TriageDimension | null>(null);
  const [activeSurface, setActiveSurface] = useState<TriageSurface>(() =>
    getActiveSurfaceForTriage(initialSelectedItemId, null)
  );
  const [filterMode, setFilterMode] = useState<"all" | "undecided">("all");
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(
    () => new Set(review.sidebarGroups.filter((group) => !group.defaultExpanded).map((group) => group.id))
  );
  const [decisions, setDecisions] = useState<Map<string, DecisionEntry>>(() =>
    buildInitialDecisions(review.sidebarGroups)
  );
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishSheetOpen, setIsPublishSheetOpen] = useState(false);
  const [isAddModuleModalOpen, setIsAddModuleModalOpen] = useState(false);
  const [pendingModuleDelete, setPendingModuleDelete] = useState<PendingModuleDeleteState | null>(null);
  const [pendingActivityDelete, setPendingActivityDelete] = useState<PendingActivityDeleteState | null>(null);
  const [isDeletingModernizedActivity, setIsDeletingModernizedActivity] = useState(false);
  const [locallyPendingDeleteModuleIds, setLocallyPendingDeleteModuleIds] = useState<Set<string>>(() => new Set());
  const [locallyPendingDeleteActivityIds, setLocallyPendingDeleteActivityIds] = useState<Set<string>>(() => new Set());
  const [locallyPublishedDeleteModuleIds, setLocallyPublishedDeleteModuleIds] = useState<Set<string>>(() => new Set());
  const [locallyPublishedDeleteActivityIds, setLocallyPublishedDeleteActivityIds] = useState<Set<string>>(
    () => new Set()
  );
  const [assignmentModule, setAssignmentModule] = useState<CourseEvaluatorReviewSidebarGroup | null>(null);
  const [addActivityModule, setAddActivityModule] = useState<CourseEvaluatorReviewSidebarGroup | null>(null);
  const [pendingCreatedActivity, setPendingCreatedActivity] = useState<{
    id: string;
    isManuallyAuthored: boolean;
  } | null>(null);
  const [ileCreationItemId, setIleCreationItemId] = useState<string | null>(null);
  const [ileGenerationItemId, setIleGenerationItemId] = useState<string | null>(null);
  const [generatedIlePrefillsByRecommendationId, setGeneratedIlePrefillsByRecommendationId] = useState<
    Record<string, GeneratedIlePrefill>
  >({});
  const [isLivePublishWarningVisible, setIsLivePublishWarningVisible] = useState(false);
  const [publishStatus, setPublishStatus] = useState<PublishStatus>("idle");
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingImscc, setIsExportingImscc] = useState(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [debouncedTeamSearchQuery] = useDebounce(teamSearchQuery, 400);
  const [selectedTeamRole, setSelectedTeamRole] = useState<EditingTeamRole>(EditingTeamRole.InstructionalDesigner);
  const [liveActivityDetails, setLiveActivityDetails] = useState<Record<string, ActivityDetail>>(activityDetails);
  /** Publish history record shown in the success state, captured after publish refetch. */
  const [lastPublishRecord, setLastPublishRecord] = useState<PublishHistoryRecord | null>(null);
  const [lastPublishedFallbackItems, setLastPublishedFallbackItems] = useState<PublishedPublishItem[]>([]);
  const closePublishSheetBeforeViewingSnapshot = useCallback(() => {
    setIsPublishSheetOpen(false);
  }, []);
  const currentUserId = userInfo.userId || null;
  const {
    data: editingTeamData,
    loading: isEditingTeamLoading,
    refetch: refetchEditingTeam,
  } = useGetModernizedCourseEditingTeamQuery({
    variables: { courseId: review.courseId },
    fetchPolicy: "cache-and-network",
  });
  const editingTeamResult = editingTeamData?.getModernizedCourseEditingTeam;
  const permissions = useMemo(
    () =>
      buildCourseModernizerReviewPermissions({
        architectId: editingTeamResult?.success ? editingTeamResult.architectId : null,
        currentUserId,
        isLoading: isEditingTeamLoading && !editingTeamResult,
        members: editingTeamResult?.success ? editingTeamResult.members : null,
      }),
    [currentUserId, editingTeamResult, isEditingTeamLoading]
  );
  const teamManagement = review.teamManagement;
  const isSavingTeamManagement = isSettingEditingTeamMember || isRemovingEditingTeamMember;
  const schoolId = teamManagement?.schoolId ?? "";
  const trimmedTeamSearchQuery = debouncedTeamSearchQuery.trim();
  const { data: teamUsersData } = useGetUsersWithElevatedRolesQuery({
    variables: {
      schoolId,
      searchQuery: trimmedTeamSearchQuery,
      page: 1,
      pageSize: 20,
      sortBy: "name_asc",
    },
    skip: !permissions.canManageTeam || !schoolId || !trimmedTeamSearchQuery,
    fetchPolicy: "cache-and-network",
  });
  const teamUserOptions = useMemo<TeamManagementUserOption[]>(() => {
    const options = new Map<string, TeamManagementUserOption>();
    const isFacultyOrAbove = (roleName?: string | null) =>
      roleName ? hasMinRole(roleName as UserRoleUI, UserRoleUI.FACULTY) : false;
    const addOption = (id: string | null | undefined, label: string | null | undefined, subtitle?: string | null) => {
      if (!id) {
        return;
      }

      options.set(id, {
        id,
        label: label || subtitle || "Unnamed user",
        subtitle: subtitle ?? null,
      });
    };

    addOption(teamManagement?.architect?.id, teamManagement?.architect?.name, teamManagement?.architect?.email);
    teamManagement?.editingTeam.forEach((member) => {
      addOption(member.user.id, member.user.name, member.user.email);
    });
    (teamUsersData?.getUsersWithElevatedRoles?.data?.faculties ?? [])
      .filter((user) => user?.hasRoleRoles?.some((role) => isFacultyOrAbove(role?.name)))
      .forEach((faculty) => {
        addOption(faculty.id, faculty.name, faculty.email);
      });

    return Array.from(options.values());
  }, [teamManagement, teamUsersData]);
  const decisionSourceSignature = useMemo(
    () => getDecisionSourceSignature(review.sidebarGroups),
    [review.sidebarGroups]
  );

  // Ref to track latest decisions for non-blocking auto-advance
  const decisionsRef = useRef(decisions);
  decisionsRef.current = decisions;

  const isMountedRef = useRef(true);
  const decisionSourceSignatureRef = useRef(decisionSourceSignature);
  const nextUrlWriteModeRef = useRef<"push" | "replace">("replace");
  const isApplyingPopstateRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (decisionSourceSignatureRef.current === decisionSourceSignature) {
      return;
    }

    decisionSourceSignatureRef.current = decisionSourceSignature;
    setDecisions(buildInitialDecisions(review.sidebarGroups));
  }, [decisionSourceSignature, review.sidebarGroups]);

  useEffect(() => {
    setLiveActivityDetails((current) =>
      Object.fromEntries(
        Object.entries(activityDetails).map(([activityId, detail]) => [
          activityId,
          current[activityId]?.isManuallyAuthored ? { ...detail, isManuallyAuthored: true } : detail,
        ])
      )
    );
  }, [activityDetails]);

  useEffect(() => {
    setGeneratedIlePrefillsByRecommendationId({});
  }, [activityDetails]);

  const augmentedSidebarGroups = useMemo<CourseEvaluatorReviewSidebarGroup[]>(
    () =>
      review.sidebarGroups.flatMap((group) => {
        if (locallyPublishedDeleteModuleIds.has(group.id)) {
          return [];
        }
        const moduleItem = getModuleDescriptionItem(group);
        if (moduleItem && isTerminalDeletedItem(moduleItem, decisions.get(moduleItem.id)?.decision ?? null)) {
          return [];
        }

        return [
          {
            ...group,
            pendingDelete: group.pendingDelete === true || locallyPendingDeleteModuleIds.has(group.id),
            items: group.items.flatMap((item) => {
              if (locallyPublishedDeleteActivityIds.has(item.id)) {
                return [];
              }

              const augmentedItem = augmentManualSidebarItem(item, liveActivityDetails[item.id]);
              if (isTerminalDeletedItem(augmentedItem, decisions.get(augmentedItem.id)?.decision ?? null)) {
                return [];
              }

              if (locallyPendingDeleteActivityIds.has(augmentedItem.id)) {
                return [{ ...augmentedItem, pendingDelete: true }];
              }

              if (
                augmentedItem.entityType !== ModernizedActivityEntityType.Module ||
                !locallyPendingDeleteModuleIds.has(augmentedItem.id)
              ) {
                return [augmentedItem];
              }

              return [{ ...augmentedItem, pendingDelete: true }];
            }),
          },
        ];
      }),
    [
      liveActivityDetails,
      locallyPendingDeleteActivityIds,
      locallyPendingDeleteModuleIds,
      locallyPublishedDeleteActivityIds,
      locallyPublishedDeleteModuleIds,
      decisions,
      review.sidebarGroups,
    ]
  );

  const allItems = useMemo(() => augmentedSidebarGroups.flatMap((group) => group.items), [augmentedSidebarGroups]);

  const orderedIds = useMemo(() => allItems.map((item) => item.id), [allItems]);
  const validItemIds = useMemo(() => new Set(orderedIds), [orderedIds]);

  const setSelectedItemForTriage = useCallback(
    (itemId: string | null, mode: "push" | "replace" = "push") => {
      nextUrlWriteModeRef.current = mode;
      setSelectedItemId(itemId);
      setActiveSurface(getActiveSurfaceForTriage(itemId, selectedDimension));
    },
    [selectedDimension]
  );

  const writeTriageQueryToUrl = useCallback(
    (mode: "push" | "replace") => {
      if (Platform.OS !== "web" || typeof window === "undefined") {
        return;
      }

      const nextSearch = buildTriageQueryState(window.location.search, {
        courseId: review.courseId,
        dimension: selectedDimension,
        entityId: selectedItemId,
        surface: activeSurface,
        sheet: selectedItemId ? "open" : "closed",
      });

      if (nextSearch === window.location.search) {
        return;
      }

      const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
      if (mode === "push") {
        window.history.pushState({}, "", nextUrl);
        return;
      }

      window.history.replaceState({}, "", nextUrl);
    },
    [activeSurface, review.courseId, selectedDimension, selectedItemId]
  );

  useEffect(() => {
    if (selectedItemId && !validItemIds.has(selectedItemId)) {
      // If popstate landed on an invalid entity, allow the normalization write.
      if (isApplyingPopstateRef.current) {
        isApplyingPopstateRef.current = false;
      }
      setSelectedItemForTriage(null, "replace");
    }
  }, [selectedItemId, setSelectedItemForTriage, validItemIds]);

  useEffect(() => {
    if (!pendingCreatedActivity || !validItemIds.has(pendingCreatedActivity.id)) {
      return;
    }

    const createdActivityDetail = activityDetails[pendingCreatedActivity.id];
    if (!createdActivityDetail) {
      return;
    }

    setLiveActivityDetails((current) => ({
      ...current,
      [pendingCreatedActivity.id]: pendingCreatedActivity.isManuallyAuthored
        ? { ...createdActivityDetail, isManuallyAuthored: true }
        : createdActivityDetail,
    }));
    setSelectedItemForTriage(pendingCreatedActivity.id, "replace");
    setPendingCreatedActivity(null);
  }, [activityDetails, pendingCreatedActivity, setSelectedItemForTriage, validItemIds]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    if (isApplyingPopstateRef.current) {
      isApplyingPopstateRef.current = false;
      return;
    }

    const mode = nextUrlWriteModeRef.current;
    nextUrlWriteModeRef.current = "replace";
    writeTriageQueryToUrl(mode);
  }, [writeTriageQueryToUrl]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    const handlePopstate = () => {
      const queryState = parseTriageQueryState(window.location.search);
      isApplyingPopstateRef.current = true;
      setSelectedDimension(queryState.dimension);
      setActiveSurface(queryState.surface);
      setSelectedItemId(queryState.sheet === "open" ? queryState.entityId : null);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, []);

  /** Lookup table mapping each item id to its enclosing module info. */
  const itemModuleIndex = useMemo(() => {
    const map = new Map<string, { moduleId: string | null; moduleName: string; moduleExternalUrl?: string }>();
    augmentedSidebarGroups.forEach((group) => {
      const moduleId = group.id === "course-level-content" ? null : group.id;
      group.items.forEach((item) => {
        map.set(item.id, { moduleId, moduleName: group.title, moduleExternalUrl: group.externalUrl });
      });
    });
    return map;
  }, [augmentedSidebarGroups]);

  const liveSidebarGroups = useMemo<CourseEvaluatorReviewSidebarGroup[]>(() => {
    return augmentedSidebarGroups
      .map((group) => {
        const evaluatorItems = group.items.filter((item) => item.pendingDelete !== true);
        const decided = evaluatorItems.filter((item) => decisions.get(item.id)?.decision !== undefined).length;
        const items =
          filterMode === "undecided"
            ? evaluatorItems.filter((item) => decisions.get(item.id)?.decision === undefined)
            : group.items;

        return { ...group, decided, total: evaluatorItems.length, items };
      })
      .filter((group) => filterMode === "all" || group.items.length > 0 || group.isNew === true);
  }, [augmentedSidebarGroups, filterMode, decisions]);

  const selectedSidebarItem = useMemo<CourseEvaluatorReviewSidebarItem | null>(() => {
    if (!selectedItemId) return null;
    return allItems.find((item) => item.id === selectedItemId) || null;
  }, [allItems, selectedItemId]);

  const getDecision = useCallback(
    (id: string): CourseEvaluatorReviewDecision => decisions.get(id)?.decision ?? null,
    [decisions]
  );

  const selectedItemRequiresIleCreation = isIleRecommendationItem(selectedSidebarItem);
  const selectedItemIsPendingDelete = selectedSidebarItem?.pendingDelete === true;
  const selectedItemDetail = selectedItemId ? liveActivityDetails[selectedItemId] : null;
  const selectedItemHasCreatedIle = Boolean(selectedItemDetail?.createdIleId);
  const selectedItemIsExistingImportedIle = isExistingImportedIleItem(selectedSidebarItem, selectedItemDetail);
  const canAcceptSelectedItem =
    !selectedItemIsPendingDelete &&
    (!selectedItemRequiresIleCreation || (!selectedItemIsExistingImportedIle && selectedItemHasCreatedIle));
  const acceptHiddenReason = selectedItemIsPendingDelete
    ? "Pending-delete activities are published as deletes."
    : selectedItemIsExistingImportedIle
      ? "Existing ILE edits are saved directly and do not need Brightspace publish."
      : undefined;
  const acceptDisabledReason =
    selectedItemRequiresIleCreation && !selectedItemHasCreatedIle
      ? "Create the ILE before adding it to queue."
      : undefined;

  const ileCreationDetail = ileCreationItemId ? liveActivityDetails[ileCreationItemId] : null;
  const generatedIlePrefill = ileCreationItemId ? generatedIlePrefillsByRecommendationId[ileCreationItemId] : undefined;
  const ileCreationInitialValues = useMemo<ActivityDataInput>(() => {
    if (!ileCreationDetail) {
      return {};
    }

    const createdObjectives = ileCreationDetail.createdIleObjectives ?? [];
    const hasCreatedIle = Boolean(ileCreationDetail.createdIleId);
    const prefilledObjectives = generatedIlePrefill?.objectives ?? [];

    return {
      id: ileCreationDetail.createdIleId,
      title: hasCreatedIle
        ? (ileCreationDetail.createdIleTitle ?? ileCreationDetail.recommendedTitle ?? ileCreationDetail.currentTitle)
        : (ileCreationDetail.recommendedTitle ?? ileCreationDetail.currentTitle),
      description: hasCreatedIle
        ? (ileCreationDetail.createdIleDescription ?? "")
        : (generatedIlePrefill?.description ??
          ileCreationDetail.currentDescription ??
          ileCreationDetail.recommendedDescription ??
          ""),
      context:
        ileCreationDetail.createdIleContext ??
        [ileCreationDetail.documentReference, ileCreationDetail.feedback].filter(Boolean).join("\n\n"),
      dueDate: ileCreationDetail.createdIleDueDate ?? null,
      targetAudience:
        ileCreationDetail.createdIleTargetAudience ?? mapCourseTargetAudienceToIle(review.targetAudience) ?? undefined,
      activityState: ileCreationDetail.createdIleActivityState ?? undefined,
      scheduledDate: ileCreationDetail.createdIleScheduledDate ?? undefined,
      objectives: hasCreatedIle
        ? createdObjectives.map((objective, index) => ({
            id: objective.id,
            objective: objective.objective,
            sequence: objective.sequence ?? index + 1,
            evalCriteria: objective.evalCriteria ?? [],
          }))
        : prefilledObjectives.map((objective, index) => ({
            objective: objective.objective,
            sequence: objective.sequence ?? index + 1,
            evalCriteria: objective.evalCriteria ?? [],
          })),
    };
  }, [
    generatedIlePrefill,
    generatedIlePrefillsByRecommendationId,
    ileCreationDetail,
    ileCreationItemId,
    review.targetAudience,
  ]);

  // --- Stats ---
  const stats = useMemo(() => {
    let accepted = 0;
    let rejected = 0;
    let published = 0;
    const evaluatorItems = allItems.filter((item) => item.pendingDelete !== true);
    evaluatorItems.forEach((item) => {
      const d = decisions.get(item.id)?.decision;
      if ((d === "accepted" || d === "edited") && !isExistingImportedIleItem(item, liveActivityDetails[item.id])) {
        accepted++;
      }
      if (d === "rejected") rejected++;
      if (d === "synced") published++;
    });
    const decided = accepted + rejected + published;
    return { decided, accepted, rejected, published, total: evaluatorItems.length };
  }, [allItems, decisions, liveActivityDetails]);

  const reviewedProgress = stats.total > 0 ? (stats.decided / stats.total) * 100 : 0;

  // --- Queued items for publish sheet ---
  const queuedPublishItems = useMemo<QueuedPublishItem[]>(() => {
    return allItems
      .filter((item) => {
        const d = decisions.get(item.id)?.decision;
        if (isExistingImportedIleItem(item, liveActivityDetails[item.id])) {
          return false;
        }
        if (item.pendingDelete === true && isSupportedActivityDeleteItem(item)) {
          return d !== "synced";
        }
        if (item.entityType === ModernizedActivityEntityType.Module && item.pendingDelete === true) {
          return d !== "synced";
        }
        return d === "accepted" || d === "edited";
      })
      .map((item) => {
        const mod = itemModuleIndex.get(item.id);
        const detail = liveActivityDetails[item.id];
        const moduleName =
          mod?.moduleId != null
            ? (liveActivityDetails[mod.moduleId]?.recommendedTitle ?? mod.moduleName)
            : (mod?.moduleName ?? "Course-Level Content");
        return {
          id: item.id,
          title: detail?.recommendedTitle ?? item.title,
          kind: item.kind,
          entityType: item.entityType,
          itemType: getSidebarItemType(item),
          isNew: getSidebarItemIsNew(item),
          category: getCategoryLabel(item),
          pendingDelete: item.pendingDelete === true,
          moduleId: mod?.moduleId ?? null,
          moduleName,
          moduleExternalUrl: mod?.moduleExternalUrl,
          externalUrl: detail?.url,
          attachments: detail?.attachments,
          wasEdited: decisions.get(item.id)?.decision === "edited",
          externalVisibility: item.externalVisibility,
        };
      });
  }, [allItems, decisions, itemModuleIndex, liveActivityDetails]);

  // Override review data with live stats + filtered sidebar
  const liveReview = useMemo<CourseEvaluatorReviewData>(
    () => ({
      ...review,
      reviewedCount: stats.decided,
      queuedCount: queuedPublishItems.length,
      skippedCount: stats.rejected,
      publishedCount: stats.published,
      sidebarGroups: liveSidebarGroups,
    }),
    [review, stats, queuedPublishItems.length, liveSidebarGroups]
  );
  const {
    displayActivityDetails,
    displayGetDecision,
    displayReview,
    displayReviewedProgress,
    displaySelectedDecision,
    displaySelectedSidebarItem,
    isFetchingCourseSnapshotReview,
    isRestoreSnapshotModalOpen,
    isRestoreSnapshotPending,
    isVersionHistoryOpen,
    viewingSnapshot,
    handleBackToCurrentSnapshot,
    handleCancelRestoreSnapshot,
    handleCloseVersionHistory,
    handleConfirmRestoreSnapshot,
    handleOpenRestoreSnapshotConfirmation,
    handleOpenVersionHistory: openVersionHistory,
    handleViewSnapshot,
  } = useHistoricalSnapshotReview({
    courseId: review.courseId,
    review,
    activityDetails,
    liveReview,
    liveActivityDetails,
    liveSelectedSidebarItem: selectedSidebarItem,
    liveReviewedProgress: reviewedProgress,
    selectedItemId,
    filterMode,
    getLiveDecision: getDecision,
    onRefreshReview,
    showToast,
    onBeforeViewSnapshot: closePublishSheetBeforeViewingSnapshot,
  });

  // --- Non-blocking auto-advance ---
  const advanceToNextUndecided = useCallback(
    (fromId: string) => {
      // Use requestAnimationFrame to keep the UI responsive
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        const currentDecisions = decisionsRef.current;
        const currentIdx = orderedIds.indexOf(fromId);

        // Search forward first
        for (let i = currentIdx + 1; i < orderedIds.length; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemForTriage(id);
            return;
          }
        }
        // Wrap around
        for (let i = 0; i < currentIdx; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemForTriage(id);
            return;
          }
        }
        // All decided — stay on current or go to overview
      });
    },
    [orderedIds]
  );

  const persistDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      const status = getStatusFromDecision(decision);
      const result = await updateModernizedActivityFieldsMutation({
        variables: {
          entityId: item.id,
          entityType: item.entityType,
          courseId: review.courseId,
          status,
        },
        optimisticResponse: {
          updateModernizedActivityFields: {
            __typename: "ModernizedEditLogResult",
            success: true,
          },
        },
        update: (cache) => updateActivityStatusInCache(cache, item, status),
      });

      if (!result.data?.updateModernizedActivityFields.success) {
        throw new Error("updateModernizedActivityFields mutation failed");
      }
    },
    [review.courseId, updateModernizedActivityFieldsMutation]
  );

  const markSaved = useCallback(() => {
    const now = new Date();
    setLastSavedAt(now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
  }, []);

  const handleOpenAddModule = useCallback(() => {
    if (!permissions.canEdit) return;
    setIsAddModuleModalOpen(true);
  }, [permissions.canEdit]);

  const handleOpenAddAssignment = useCallback(() => {
    if (!permissions.canEdit) return;
    if (!addActivityModule) return;
    setAssignmentModule(addActivityModule);
    setAddActivityModule(null);
  }, [addActivityModule, permissions.canEdit]);

  const handleCloseAddAssignment = useCallback(() => {
    if (isCreatingModernizedAssignment) return;
    setAssignmentModule(null);
  }, [isCreatingModernizedAssignment]);

  const handleCloseAddModule = useCallback(() => {
    if (isCreatingModernizedModule) return;
    setIsAddModuleModalOpen(false);
  }, [isCreatingModernizedModule]);

  const handleSaveModernizedModule = useCallback(
    async ({ title, description }: ModernizerAddModuleInput) => {
      if (!permissions.canEdit) return;

      try {
        const result = await createModernizedModuleMutation({
          variables: {
            courseId: review.courseId,
            title,
            description,
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.createModernizedModule;
        if (!payload?.success || !payload.module?.id) {
          throw new Error(payload?.message ?? "Failed to create module");
        }

        if (!isMountedRef.current) return;

        setIsAddModuleModalOpen(false);
        markSaved();
        showToast({
          message: "Module added to the modernized course.",
          type: "success",
        });
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to add module. Please try again."),
          type: "error",
        });
      }
    },
    [createModernizedModuleMutation, markSaved, permissions.canEdit, review.courseId, showToast]
  );

  const handleModuleDragEnd = useCallback(
    async (groups: CourseEvaluatorReviewSidebarGroup[]) => {
      if (!permissions.canEdit) return;
      const moduleIds = groups.map((group) => group.id);
      if (moduleIds.length <= 1) return;

      setIsSaving(true);
      try {
        const result = await reorderModernizedModulesMutation({
          variables: {
            courseId: review.courseId,
            moduleIds,
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.reorderModernizedModules;
        if (!payload?.success) {
          throw new Error(payload?.message ?? "Failed to reorder modules");
        }

        if (!isMountedRef.current) return;
        markSaved();
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to reorder modules. Please try again."),
          type: "error",
        });
        throw error;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, permissions.canEdit, reorderModernizedModulesMutation, review.courseId, showToast]
  );

  const handleActivityDragEnd = useCallback(
    async (group: CourseEvaluatorReviewSidebarGroup, items: CourseEvaluatorReviewSidebarItem[]) => {
      if (!permissions.canEdit) return;
      const activityIds = items.map((item) => item.id);
      if (activityIds.length <= 1) return;

      setIsSaving(true);
      try {
        const result = await reorderModernizedModuleActivitiesMutation({
          variables: {
            courseId: review.courseId,
            moduleId: group.id,
            activityIds,
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.reorderModernizedModuleActivities;
        if (!payload?.success) {
          throw new Error(payload?.message ?? "Failed to reorder activities");
        }

        if (!isMountedRef.current) return;
        markSaved();
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to reorder activities. Please try again."),
          type: "error",
        });
        throw error;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, permissions.canEdit, reorderModernizedModuleActivitiesMutation, review.courseId, showToast]
  );

  const handleActivityMoveEnd = useCallback(
    async ({ sourceGroup, targetGroup, movedItem, sourceItems, targetItems }: CourseEvaluatorReviewActivityMove) => {
      if (!permissions.canEdit) return;
      if (sourceGroup.id === targetGroup.id) return;

      setIsSaving(true);
      try {
        const result = await moveModernizedModuleActivityMutation({
          variables: {
            courseId: review.courseId,
            sourceModuleId: sourceGroup.id,
            targetModuleId: targetGroup.id,
            activityId: movedItem.id,
            sourceActivityIds: sourceItems.map((item) => item.id),
            targetActivityIds: targetItems.map((item) => item.id),
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.moveModernizedModuleActivity;
        if (!payload?.success) {
          throw new Error(payload?.message ?? "Failed to move activity");
        }

        if (!isMountedRef.current) return;
        markSaved();
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to move activity. Please try again."),
          type: "error",
        });
        throw error;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, moveModernizedModuleActivityMutation, permissions.canEdit, review.courseId, showToast]
  );

  const handleSaveModernizedAssignment = useCallback(
    async ({ title, descriptionHtml, dueDate, videoUrl, enableFileUpload, files }: ModernizerAddAssignmentInput) => {
      if (!permissions.canEdit) return;
      if (!assignmentModule) return;

      try {
        const result = await createModernizedAssignmentActivityMutation({
          variables: {
            input: {
              courseId: review.courseId,
              moduleId: assignmentModule.id,
              title,
              descriptionHtml,
              dueDate,
              videoUrl,
              enableFileUpload,
              files: files.map(({ id }) => ({ fileId: id })),
              submissionKinds: enableFileUpload ? [ExternalAssignmentSubmissionKind.FileUpload] : [],
            },
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.createModernizedAssignmentActivity;
        if (!payload?.success || !payload.activity?.id) {
          throw new Error(payload?.message ?? "Failed to create assignment");
        }

        if (!isMountedRef.current) return;

        setPendingCreatedActivity({
          id: payload.activity.id,
          isManuallyAuthored: true,
        });
        setAssignmentModule(null);
        markSaved();
        showToast({
          message: `Assignment added to ${assignmentModule.title}.`,
          type: "success",
        });
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to add assignment. Please try again."),
          type: "error",
        });
      }
    },
    [
      assignmentModule,
      createModernizedAssignmentActivityMutation,
      markSaved,
      permissions.canEdit,
      review.courseId,
      showToast,
    ]
  );

  const handleDeleteActivity = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      if (!permissions.canEdit) return;
      if (!isSupportedActivityDeleteItem(item) || item.pendingDelete === true) return;

      setPendingActivityDelete({
        item,
        activityLabel: getActivityDeleteLabel(item),
      });
    },
    [permissions.canEdit]
  );

  const handleCloseActivityDeleteModal = useCallback(() => {
    if (isDeletingModernizedActivity) return;
    setPendingActivityDelete(null);
  }, [isDeletingModernizedActivity]);

  const handleConfirmDeleteActivity = useCallback(async () => {
    if (!permissions.canEdit) return;
    if (!pendingActivityDelete) return;

    const { item, activityLabel } = pendingActivityDelete;

    try {
      setIsDeletingModernizedActivity(true);
      const mutationOptions = {
        variables: {
          courseId: review.courseId,
          activityId: item.id,
        },
        refetchQueries: [
          {
            query: GetModernizedCourseDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      };
      const payload: ActivityDeletePayload | null | undefined =
        item.entityType === ModernizedActivityEntityType.Project
          ? (await deleteModernizedProjectActivityMutation(mutationOptions)).data?.deleteModernizedProjectActivity
          : item.entityType === ModernizedActivityEntityType.OtherActivity
            ? (await deleteModernizedPageActivityMutation(mutationOptions)).data?.deleteModernizedPageActivity
            : item.entityType === ModernizedActivityEntityType.IleRecommendation
              ? (await deleteModernizedIleActivityMutation(mutationOptions)).data?.deleteModernizedIleActivity
              : (await deleteModernizedAssignmentActivityMutation(mutationOptions)).data
                  ?.deleteModernizedAssignmentActivity;

      if (!payload?.success || (payload.activityId !== item.id && payload.deletedActivityId !== item.id)) {
        throw new Error(payload?.message ?? `Failed to delete ${activityLabel}`);
      }

      if (!isMountedRef.current) return;

      if (payload.wasDiscarded) {
        if (selectedItemId === item.id) {
          setSelectedItemForTriage(null, "replace");
        }
        setLocallyPendingDeleteActivityIds((current) => {
          const next = new Set(current);
          next.delete(item.id);
          return next;
        });
        setDecisions((current) => {
          const next = new Map(current);
          next.delete(item.id);
          return next;
        });
        setLiveActivityDetails((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
      } else {
        setLocallyPendingDeleteActivityIds((current) => new Set(current).add(item.id));
      }

      setUndoEntry(null);
      setPendingActivityDelete(null);
      markSaved();
      showToast({
        message: payload.wasDiscarded
          ? `${sentenceCaseActivityLabel(activityLabel)} discarded.`
          : `${sentenceCaseActivityLabel(activityLabel)} marked as pending delete.`,
        type: "success",
      });
    } catch (error) {
      if (!isMountedRef.current) return;
      showToast({
        message: getErrorMessage(error, `Failed to delete ${activityLabel}. Please try again.`),
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setIsDeletingModernizedActivity(false);
      }
    }
  }, [
    deleteModernizedAssignmentActivityMutation,
    deleteModernizedProjectActivityMutation,
    deleteModernizedIleActivityMutation,
    deleteModernizedPageActivityMutation,
    markSaved,
    pendingActivityDelete,
    permissions.canEdit,
    review.courseId,
    selectedItemId,
    setSelectedItemForTriage,
    showToast,
  ]);

  const handleUndoDeleteActivity = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem) => {
      if (!permissions.canRestoreVersion) return;
      if (!isSupportedActivityDeleteItem(item) || item.pendingDelete !== true) return;

      const activityLabel = getActivityDeleteLabel(item);

      try {
        const result = await undoDeleteModernizedActivityMutation({
          variables: {
            courseId: review.courseId,
            activityId: item.id,
            entityType: item.entityType,
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.undoDeleteModernizedActivity;
        if (!payload?.success || payload.activityId !== item.id) {
          throw new Error(payload?.message ?? `Failed to restore ${activityLabel}`);
        }

        if (!isMountedRef.current) return;

        setLocallyPendingDeleteActivityIds((current) => {
          const next = new Set(current);
          next.delete(item.id);
          return next;
        });
        setUndoEntry(null);
        markSaved();
        showToast({
          message: `${sentenceCaseActivityLabel(activityLabel)} delete undone.`,
          type: "success",
        });
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, `Failed to restore ${activityLabel}. Please try again.`),
          type: "error",
        });
      }
    },
    [markSaved, permissions.canRestoreVersion, review.courseId, showToast, undoDeleteModernizedActivityMutation]
  );

  const handleDeleteModule = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      if (!permissions.canEdit) return;
      const sourceGroup = augmentedSidebarGroups.find((candidate) => candidate.id === group.id) ?? group;
      const moduleItem = getModuleDescriptionItemOrFallback(sourceGroup);

      const activeActivityCount = countActiveModuleActivities(sourceGroup, decisionsRef.current);
      const stagedActivityCount = sourceGroup.isNew === true ? countModuleActivities(sourceGroup) : 0;
      setPendingModuleDelete({
        group: sourceGroup,
        moduleItem,
        activeActivityCount,
        stagedActivityCount,
        mode: stagedActivityCount > 0 || activeActivityCount > 0 ? "blocked" : "confirm",
      });
    },
    [augmentedSidebarGroups, permissions.canEdit]
  );

  const handleCloseModuleDeleteModal = useCallback(() => {
    if (isDeletingModernizedModule) return;
    setPendingModuleDelete(null);
  }, [isDeletingModernizedModule]);

  const handleConfirmDeleteModule = useCallback(async () => {
    if (!permissions.canEdit) return;
    if (!pendingModuleDelete || pendingModuleDelete.mode !== "confirm") return;

    const { group, moduleItem } = pendingModuleDelete;

    try {
      const result = await deleteModernizedModuleMutation({
        variables: {
          courseId: review.courseId,
          moduleId: moduleItem.id,
        },
        refetchQueries: [
          {
            query: GetModernizedCourseDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      });

      const payload = result.data?.deleteModernizedModule;
      if (!payload?.success || payload.moduleId !== moduleItem.id) {
        throw new Error(payload?.message ?? "Failed to delete module");
      }

      if (!isMountedRef.current) return;

      if (payload.wasDiscarded) {
        const groupItemIds = new Set(group.items.map((item) => item.id));
        if (selectedItemId && groupItemIds.has(selectedItemId)) {
          setSelectedItemForTriage(null, "replace");
        }
        setLocallyPendingDeleteModuleIds((current) => {
          const next = new Set(current);
          next.delete(moduleItem.id);
          return next;
        });
        setDecisions((current) => {
          const next = new Map(current);
          group.items.forEach((item) => next.delete(item.id));
          return next;
        });
        setUndoEntry(null);
      } else {
        setLocallyPendingDeleteModuleIds((current) => new Set(current).add(moduleItem.id));
        setUndoEntry(null);
      }

      setPendingModuleDelete(null);
      markSaved();
      showToast({
        message: payload.wasDiscarded ? "Module discarded." : "Module marked as pending delete.",
        type: "success",
      });
    } catch (error) {
      if (!isMountedRef.current) return;
      showToast({
        message: getErrorMessage(error, "Failed to delete module. Please try again."),
        type: "error",
      });
    }
  }, [
    deleteModernizedModuleMutation,
    markSaved,
    pendingModuleDelete,
    permissions.canEdit,
    review.courseId,
    selectedItemId,
    setSelectedItemForTriage,
    showToast,
  ]);

  const restorePendingDeletedModule = useCallback(
    async (moduleItem: CourseEvaluatorReviewSidebarItem, successMessage: string): Promise<boolean> => {
      if (!permissions.canRestoreVersion) return false;

      const result = await undoDeleteModernizedModuleMutation({
        variables: {
          courseId: review.courseId,
          moduleId: moduleItem.id,
        },
        refetchQueries: [
          {
            query: GetModernizedCourseDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      });

      const payload = result.data?.undoDeleteModernizedModule;
      if (!payload?.success || payload.moduleId !== moduleItem.id) {
        throw new Error(payload?.message ?? "Failed to restore module");
      }

      if (!isMountedRef.current) return false;

      setLocallyPendingDeleteModuleIds((current) => {
        const next = new Set(current);
        next.delete(moduleItem.id);
        return next;
      });
      setUndoEntry(null);
      markSaved();
      showToast({
        message: successMessage,
        type: "success",
      });

      return true;
    },
    [markSaved, permissions.canRestoreVersion, review.courseId, showToast, undoDeleteModernizedModuleMutation]
  );

  const handleUndoDeleteModule = useCallback(
    async (group: CourseEvaluatorReviewSidebarGroup) => {
      if (!permissions.canRestoreVersion) return;
      const sourceGroup = augmentedSidebarGroups.find((candidate) => candidate.id === group.id) ?? group;
      const moduleItem = getModuleDescriptionItem(sourceGroup);
      if (!moduleItem) return;

      try {
        await restorePendingDeletedModule(moduleItem, "Module delete undone.");
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to restore module. Please try again."),
          type: "error",
        });
      }
    },
    [augmentedSidebarGroups, permissions.canRestoreVersion, restorePendingDeletedModule, showToast]
  );

  const isCreatingModernizedActivity =
    isCreatingModernizedPageActivity || isCreatingModernizedProjectActivity || isCreatingModernizedIleActivity;

  const handleOpenAddActivity = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      if (!permissions.canEdit) return;
      setAddActivityModule(group);
    },
    [permissions.canEdit]
  );

  const handleCloseAddActivity = useCallback(() => {
    if (isCreatingModernizedActivity) return;
    setAddActivityModule(null);
  }, [isCreatingModernizedActivity]);

  const runCreateActivityRefetch = useCallback(
    async <TResult extends { data?: unknown }>(
      runMutation: () => Promise<TResult>,
      getActivityId: (result: TResult) => string | undefined
    ) => {
      const result = await runMutation();
      const activityId = getActivityId(result);
      if (!activityId) {
        throw new Error("Failed to create activity");
      }

      return activityId;
    },
    []
  );

  const handleSaveModernizedActivity = useCallback(
    async (activity: ModernizerCreateActivityInput) => {
      if (!permissions.canEdit) return;

      const moduleId = addActivityModule?.id;
      if (!moduleId) return;

      const withCommonInput = <
        TInput extends { courseId: string; moduleId: string; status?: ModernizedActivityStatus },
      >(
        input: Omit<TInput, "courseId" | "moduleId">
      ): TInput =>
        ({
          ...input,
          courseId: review.courseId,
          moduleId,
          status: input.status ?? ModernizedActivityStatus.Undecided,
        }) as TInput;

      try {
        const refetchQueries = [{ query: GetModernizedCourseDocument, variables: { courseId: review.courseId } }];
        const activityId =
          activity.type === "page"
            ? await runCreateActivityRefetch(
                () =>
                  createModernizedPageActivityMutation({
                    variables: {
                      input: withCommonInput<CreateModernizedPageActivityInput>(activity.input),
                    },
                    refetchQueries,
                    awaitRefetchQueries: true,
                  }),
                (result) => result.data?.createModernizedPageActivity.activity?.id
              )
            : activity.type === "project"
              ? await runCreateActivityRefetch(
                  () =>
                    createModernizedProjectActivityMutation({
                      variables: {
                        input: withCommonInput<CreateModernizedProjectActivityInput>(activity.input),
                      },
                      refetchQueries,
                      awaitRefetchQueries: true,
                    }),
                  (result) => {
                    const payload = result.data?.createModernizedProjectActivity;
                    if (!payload?.success) {
                      throw new Error(payload?.message ?? "Failed to create project");
                    }

                    return payload.activity?.id;
                  }
                )
              : await runCreateActivityRefetch(
                  () =>
                    createModernizedIleActivityMutation({
                      variables: {
                        courseId: review.courseId,
                        moduleId,
                        ile: mapActivityDataToCreateIleInput(activity.input),
                      },
                      refetchQueries,
                      awaitRefetchQueries: true,
                    }),
                  (result) => {
                    const payload = result.data?.createModernizedIleActivity;
                    if (!payload?.success) {
                      throw new Error(payload?.message ?? "Failed to create ILE");
                    }

                    return payload.activity?.id;
                  }
                );

        if (!isMountedRef.current) return;

        setPendingCreatedActivity({
          id: activityId,
          isManuallyAuthored: true,
        });
        setAddActivityModule(null);
        markSaved();
        showToast({
          message: "Activity added to the modernized course.",
          type: "success",
        });
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to add activity. Please try again."),
          type: "error",
        });
      }
    },
    [
      addActivityModule?.id,
      createModernizedIleActivityMutation,
      createModernizedPageActivityMutation,
      createModernizedProjectActivityMutation,
      markSaved,
      permissions.canEdit,
      review.courseId,
      runCreateActivityRefetch,
      showToast,
    ]
  );

  const handleSaveEdit = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFieldEdits) => {
      if (!permissions.canEdit) return;
      if (decisionsRef.current.get(item.id)?.decision === "synced") return;

      const variables = getEditMutationVariables(item, editedFields);
      if (!variables) return;

      setIsSaving(true);
      try {
        const result = await updateModernizedActivityFieldsMutation({
          variables: {
            ...variables,
            courseId: review.courseId,
          },
          update: (cache) => {
            const cacheId = cache.identify({
              __typename: getActivityCacheTypename(item.entityType),
              id: item.id,
            });
            if (!cacheId) return;

            const fields: Record<string, () => unknown> = {};
            if (variables.recommendedTitle !== undefined) {
              fields.recommendedTitle = () => variables.recommendedTitle;
            }
            if (variables.recommendedDescription !== undefined) {
              fields.recommendedDescription = () => variables.recommendedDescription;
            }
            if (variables.recommendedPointsPossible !== undefined) {
              fields.recommendedPointsPossible = () => variables.recommendedPointsPossible;
            }
            if (variables.recommendedDueDate !== undefined) {
              fields.recommendedDueDate = () => variables.recommendedDueDate;
              fields.recommendedDueDateCleared = () => variables.recommendedDueDate === null;
            }
            if (variables.recommendedValue !== undefined) {
              fields.recommendedValue = () => variables.recommendedValue;
            }
            cache.modify({ id: cacheId, fields });
          },
        });
        if (!result.data?.updateModernizedActivityFields.success) {
          throw new Error("updateModernizedActivityFields mutation failed");
        }
        if (!isMountedRef.current) return;

        setLiveActivityDetails((prev) => {
          const current = prev[item.id];
          if (!current) return prev;

          // Only treat as cleared when the field is explicitly a string (present
          // in the form) but empty — not when undefined (field was never shown).
          const dueDateCleared = typeof editedFields.dueDate === "string" && !editedFields.dueDate.trim();
          const next: ActivityDetail = { ...current };
          if (variables.recommendedTitle !== undefined) {
            next.recommendedTitle = variables.recommendedTitle ?? undefined;
          }
          if (variables.recommendedDescription !== undefined) {
            next.recommendedDescription = variables.recommendedDescription ?? undefined;
          }
          if (variables.recommendedValue !== undefined) {
            next.recommendedDescription = variables.recommendedValue ?? undefined;
          }
          if (variables.recommendedPointsPossible !== undefined) {
            next.recommendedPointsPossible = variables.recommendedPointsPossible;
          }
          if (variables.recommendedDueDate !== undefined) {
            next.recommendedDueDate = dueDateCleared ? undefined : editedFields.dueDate;
            next.recommendedDueDateCleared = dueDateCleared || undefined;
          }

          return {
            ...prev,
            [item.id]: next,
          };
        });
        markSaved();
      } catch {
        if (!isMountedRef.current) return;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, permissions.canEdit, review.courseId, updateModernizedActivityFieldsMutation]
  );

  const handleOpenIleCreation = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem) => {
      if (!permissions.canEdit) return;
      if (!isIleRecommendationItem(item)) return;

      const existingIleId = liveActivityDetails[item.id]?.createdIleId;
      if (existingIleId) {
        setIleCreationItemId(item.id);
        return;
      }

      if (generatedIlePrefillsByRecommendationId[item.id]) {
        setIleCreationItemId(item.id);
        return;
      }

      try {
        setIleGenerationItemId(item.id);
        const result = await generateIleRecommendationContentMutation({
          variables: {
            courseId: review.courseId,
            recommendationId: item.id,
          },
        });

        const payload = result.data?.generateIleRecommendationContent;
        if (!payload?.success || !payload.description) {
          throw new Error(payload?.message || "Failed to generate ILE content");
        }

        if (!isMountedRef.current) return;

        setGeneratedIlePrefillsByRecommendationId((prev) => ({
          ...prev,
          [item.id]: {
            description: payload.description,
            objectives: (payload.objectives ?? []).map((objective, index) => ({
              objective: objective.objective,
              sequence: objective.sequence ?? index + 1,
              evalCriteria: objective.evalCriteria ?? [],
            })),
          },
        }));
        setIleCreationItemId(item.id);
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to generate ILE. Please try again."),
          type: "error",
        });
      } finally {
        if (isMountedRef.current) {
          setIleGenerationItemId(null);
        }
      }
    },
    [
      generateIleRecommendationContentMutation,
      generatedIlePrefillsByRecommendationId,
      liveActivityDetails,
      permissions.canEdit,
      review.courseId,
      showToast,
    ]
  );

  const handlePreviewIle = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      const activityId = liveActivityDetails[item.id]?.createdIleId;
      if (!activityId) {
        showToast({ message: "Create the ILE before previewing it.", type: "error" });
        return;
      }

      if (isCompanionMode) {
        navigation.navigate("Interactive Learning Environment", { activityId });
        return;
      }

      enterPreviewMode(review.courseId, review.courseName);
      const moduleInfo = itemModuleIndex.get(item.id);
      navigation.navigate("Custom Course AITutor", {
        resource_type: "aiTutor",
        resource_id: activityId,
        course_id: review.courseId,
        integration_id: review.courseId,
        module_id: moduleInfo?.moduleId ?? undefined,
      });
    },
    [
      enterPreviewMode,
      isCompanionMode,
      itemModuleIndex,
      liveActivityDetails,
      navigation,
      review.courseId,
      review.courseName,
      showToast,
    ]
  );

  const handleCloseIleCreation = useCallback(() => {
    if (isCreatingIle || isUpdatingIle || isGeneratingIleRecommendationContent) return;
    setIleCreationItemId(null);
  }, [isCreatingIle, isGeneratingIleRecommendationContent, isUpdatingIle]);

  const handleSaveIleCreation = useCallback(
    async (activityData: ActivityDataInput) => {
      if (!permissions.canEdit) return;
      if (!ileCreationItemId) return;

      try {
        const currentDetail = liveActivityDetails[ileCreationItemId];
        const boundIleId = currentDetail?.createdIleId;
        const savedIle = boundIleId
          ? await (async () => {
              const result = await updateAiTutorMutation({
                variables: {
                  id: boundIleId,
                  aiTutor: mapActivityDataToUpdateIleInput(activityData),
                },
              });
              const payload = result.data?.updateAITutor;
              const updatedIle = payload?.data?.[0];
              if (!payload?.success || !updatedIle?.id) {
                throw new Error(payload?.message || "Failed to update ILE");
              }
              return {
                id: updatedIle.id,
                title: activityData.title ?? updatedIle.title,
                description: activityData.description ?? updatedIle.description,
                dueDate: normalizeDueDate(activityData.dueDate),
                targetAudience: activityData.targetAudience ?? null,
                activityState: activityData.activityState ?? null,
                scheduledDate: activityData.scheduledDate ?? null,
                objectives: activityData.objectives ?? activityData.rawObjectives ?? [],
              };
            })()
          : await (async () => {
              const result = await createIleForModernizedRecommendationMutation({
                variables: {
                  courseId: review.courseId,
                  recommendationId: ileCreationItemId,
                  ile: mapActivityDataToCreateIleInput(activityData),
                },
              });

              const payload = result.data?.createIleForModernizedRecommendation;
              if (!payload?.success || !payload.ile?.id) {
                throw new Error(payload?.message || "Failed to create ILE");
              }

              const generatedIle = payload.ile;
              const generatedObjectives = (generatedIle.objectives ?? []).map((objective, index) => ({
                id: objective?.id,
                objective: objective?.objective ?? "",
                sequence: objective?.sequence ?? index + 1,
                evalCriteria: (objective?.evalCriteria ?? []).filter((criteria): criteria is string =>
                  Boolean(criteria)
                ),
              }));

              if (isMountedRef.current) {
                setLiveActivityDetails((prev) => {
                  const current = prev[ileCreationItemId];
                  if (!current) return prev;

                  return {
                    ...prev,
                    [ileCreationItemId]: {
                      ...current,
                      createdIleId: generatedIle.id,
                      createdIleWasUserCreated: true,
                    },
                  };
                });
              }

              const updateResult = await updateAiTutorMutation({
                variables: {
                  id: generatedIle.id,
                  aiTutor: mapActivityDataToUpdateIleInput({
                    ...activityData,
                    title: activityData.title ?? generatedIle.title ?? undefined,
                    description: generatedIle.description ?? activityData.description,
                    objectives: generatedObjectives,
                  }),
                },
              });
              const updatePayload = updateResult.data?.updateAITutor;
              if (!updatePayload?.success) {
                throw new Error(updatePayload?.message || "Failed to save ILE details");
              }

              return {
                id: generatedIle.id,
                title: activityData.title ?? generatedIle.title,
                description: generatedIle.description ?? activityData.description,
                dueDate: normalizeDueDate(activityData.dueDate),
                targetAudience: activityData.targetAudience ?? generatedIle.targetAudience ?? null,
                activityState: activityData.activityState ?? generatedIle.activityState ?? null,
                scheduledDate: activityData.scheduledDate ?? generatedIle.scheduledDate ?? null,
                objectives: generatedObjectives,
              };
            })();

        if (!isMountedRef.current) return;

        setLiveActivityDetails((prev) => {
          const current = prev[ileCreationItemId];
          if (!current) return prev;

          return {
            ...prev,
            [ileCreationItemId]: {
              ...current,
              createdIleId: savedIle.id,
              createdIleWasUserCreated: current.createdIleWasUserCreated ?? !boundIleId,
              createdIleTitle: savedIle.title,
              createdIleDescription: savedIle.description,
              createdIleContext: activityData.context ?? null,
              createdIleDueDate: savedIle.dueDate,
              createdIleTargetAudience: savedIle.targetAudience,
              createdIleActivityState: savedIle.activityState,
              createdIleScheduledDate:
                savedIle.scheduledDate instanceof Date ? savedIle.scheduledDate.toISOString() : savedIle.scheduledDate,
              createdIleObjectives: savedIle.objectives.map((objective, index) => ({
                id: objective.id,
                objective: objective.objective,
                sequence: objective.sequence ?? index + 1,
                evalCriteria: objective.evalCriteria ?? [],
              })),
            },
          };
        });
        setIleCreationItemId(null);
        markSaved();
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to save the ILE. Please try again."),
          type: "error",
        });
      }
    },
    [
      createIleForModernizedRecommendationMutation,
      ileCreationItemId,
      liveActivityDetails,
      markSaved,
      permissions.canEdit,
      showToast,
      updateAiTutorMutation,
    ]
  );

  // --- Actions ---
  const makeDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      if (!permissions.canQueue) return;

      const itemId = item.id;
      const previous = decisionsRef.current.get(itemId) ?? { decision: null };
      const pendingDeleteModule =
        decision === "accepted" || decision === "edited"
          ? getPendingDeleteModuleForItem(item, augmentedSidebarGroups)
          : null;

      setUndoEntry({ itemId, previous });
      setDecisions((prev) => {
        const next = new Map(prev);
        next.set(itemId, { decision });
        return next;
      });

      // Only auto-navigate if we're viewing a specific item (not on overview)
      // This keeps the Gap Analysis drawer open when making decisions from overview
      if (selectedItemId !== null) {
        advanceToNextUndecided(itemId);
      }

      setIsSaving(true);
      let decisionPersisted = false;
      try {
        await persistDecision(item, decision);
        decisionPersisted = true;
        const restoredPendingDeleteModule = pendingDeleteModule
          ? await restorePendingDeletedModule(
              pendingDeleteModule,
              "Module delete undone because it now has an active activity."
            )
          : false;
        if (!isMountedRef.current) return;
        if (!restoredPendingDeleteModule) {
          markSaved();
        }
      } catch {
        if (!isMountedRef.current) return;
        if (decisionPersisted) {
          showToast({
            message: pendingDeleteModule
              ? `Could not undo pending delete for ${pendingDeleteModule.title}. Please undo it before publishing.`
              : "Could not finish updating this activity. Please try again.",
            type: "error",
          });
          markSaved();
          return;
        }

        setDecisions((prev) => {
          const next = new Map(prev);
          if (previous.decision === null) {
            next.delete(itemId);
          } else {
            next.set(itemId, previous);
          }
          return next;
        });
        setUndoEntry(null);
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [
      augmentedSidebarGroups,
      selectedItemId,
      advanceToNextUndecided,
      persistDecision,
      restorePendingDeletedModule,
      markSaved,
      permissions.canQueue,
      showToast,
    ]
  );

  const handleAccept = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      if (!permissions.canQueue) return;

      const detail = liveActivityDetails[item.id];
      if (isExistingImportedIleItem(item, detail)) {
        return;
      }
      if (isIleRecommendationItem(item) && !detail?.createdIleId) {
        setIleCreationItemId(item.id);
        return;
      }
      void makeDecision(item, "accepted");
    },
    [liveActivityDetails, makeDecision, permissions.canQueue]
  );

  const handleReject = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      if (!permissions.canQueue) return;
      void makeDecision(item, "rejected");
    },
    [makeDecision, permissions.canQueue]
  );

  const makeBulkDecision = useCallback(
    async (items: CourseEvaluatorReviewSidebarItem[], decision: "accepted" | "rejected") => {
      if (!permissions.canQueue) return;

      const currentDecisions = decisionsRef.current;
      const existingImportedIleItemIds =
        decision === "accepted"
          ? new Set(
              items
                .filter((item) => isExistingImportedIleItem(item, liveActivityDetails[item.id]))
                .map((item) => item.id)
            )
          : new Set<string>();
      const missingIleItems =
        decision === "accepted"
          ? items.filter(
              (item) =>
                (currentDecisions.get(item.id)?.decision ?? null) === null &&
                isIleRecommendationItem(item) &&
                !existingImportedIleItemIds.has(item.id) &&
                !liveActivityDetails[item.id]?.createdIleId
            )
          : [];
      const missingIleItemIds = new Set(missingIleItems.map((item) => item.id));
      const actionableItems = items.filter((item) => {
        const currentDecision = currentDecisions.get(item.id)?.decision ?? null;
        if (currentDecision !== null) return false;
        if (existingImportedIleItemIds.has(item.id)) return false;
        return !missingIleItemIds.has(item.id);
      });

      if (actionableItems.length === 0) {
        if (missingIleItems.length > 0) {
          showToast({
            message: getMissingIleBulkWarningMessage(missingIleItems.length),
            type: "warning",
          });
        }
        return;
      }

      const previousDecisions = new Map<string, DecisionEntry>();
      actionableItems.forEach((item) => {
        previousDecisions.set(item.id, currentDecisions.get(item.id) ?? { decision: null });
      });
      const pendingDeleteModulesToRestore =
        decision === "accepted"
          ? [
              ...new Map(
                actionableItems
                  .map((item) => getPendingDeleteModuleForItem(item, augmentedSidebarGroups))
                  .filter((moduleItem): moduleItem is CourseEvaluatorReviewSidebarItem => moduleItem !== null)
                  .map((moduleItem) => [moduleItem.id, moduleItem] as const)
              ).values(),
            ]
          : [];

      setUndoEntry(null);
      setDecisions((prev) => {
        const next = new Map(prev);
        actionableItems.forEach((item) => next.set(item.id, { decision }));
        return next;
      });

      setIsSaving(true);
      try {
        const failedItemIds = new Set<string>();
        for (const item of actionableItems) {
          try {
            await persistDecision(item, decision);
          } catch {
            failedItemIds.add(item.id);
          }
        }
        if (!isMountedRef.current) return;

        if (failedItemIds.size > 0) {
          setDecisions((prev) => {
            const next = new Map(prev);
            failedItemIds.forEach((itemId) => {
              const previous = previousDecisions.get(itemId);
              if (!previous || previous.decision === null) {
                next.delete(itemId);
              } else {
                next.set(itemId, previous);
              }
            });
            return next;
          });

          showToast({
            message: `Could not update ${failedItemIds.size} ${
              failedItemIds.size === 1 ? "activity" : "activities"
            }. Please try again.`,
            type: "error",
          });
        }

        if (actionableItems.length > failedItemIds.size) {
          markSaved();
        }

        let restoredModuleCount = 0;
        for (const moduleItem of pendingDeleteModulesToRestore) {
          const hasAcceptedActivity = actionableItems.some(
            (item) =>
              !failedItemIds.has(item.id) &&
              getPendingDeleteModuleForItem(item, augmentedSidebarGroups)?.id === moduleItem.id
          );
          if (!hasAcceptedActivity) {
            continue;
          }

          try {
            const restored = await restorePendingDeletedModule(
              moduleItem,
              "Module delete undone because it now has an active activity."
            );
            if (restored) {
              restoredModuleCount += 1;
            }
          } catch {
            showToast({
              message: `Could not undo pending delete for ${moduleItem.title}. Please undo it before publishing.`,
              type: "error",
            });
          }
        }

        if (restoredModuleCount > 0) {
          markSaved();
        }

        if (missingIleItems.length > 0) {
          showToast({
            message: getMissingIleBulkWarningMessage(missingIleItems.length),
            type: "warning",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [
      augmentedSidebarGroups,
      liveActivityDetails,
      markSaved,
      permissions.canQueue,
      persistDecision,
      restorePendingDeletedModule,
      showToast,
    ]
  );

  const getSourceGroupItems = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) =>
      review.sidebarGroups.find((sourceGroup) => sourceGroup.id === group.id)?.items ?? group.items,
    [review.sidebarGroups]
  );

  const handleAcceptGroup = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      if (!permissions.canQueue) return;
      void makeBulkDecision(getSourceGroupItems(group), "accepted");
    },
    [getSourceGroupItems, makeBulkDecision, permissions.canQueue]
  );

  const handleRejectGroup = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      if (!permissions.canQueue) return;
      void makeBulkDecision(getSourceGroupItems(group), "rejected");
    },
    [getSourceGroupItems, makeBulkDecision, permissions.canQueue]
  );

  const handleUndo = useCallback(async () => {
    if (!permissions.canQueue) return;
    if (!undoEntry) return;
    const item = allItems.find((candidate) => candidate.id === undoEntry.itemId);
    if (!item) return;
    const current = decisions.get(undoEntry.itemId) ?? { decision: null };

    setDecisions((prev) => {
      const next = new Map(prev);
      if (undoEntry.previous.decision === null) {
        next.delete(undoEntry.itemId);
      } else {
        next.set(undoEntry.itemId, undoEntry.previous);
      }
      return next;
    });
    setSelectedItemForTriage(undoEntry.itemId);
    setUndoEntry(null);

    setIsSaving(true);
    try {
      await persistDecision(item, undoEntry.previous.decision);
      if (!isMountedRef.current) return;
      markSaved();
    } catch {
      if (!isMountedRef.current) return;
      setDecisions((prev) => {
        const next = new Map(prev);
        if (current.decision === null) {
          next.delete(undoEntry.itemId);
        } else {
          next.set(undoEntry.itemId, current);
        }
        return next;
      });
      setUndoEntry(undoEntry);
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [undoEntry, allItems, decisions, persistDecision, markSaved, permissions.canQueue]);

  const handleNavigateNext = useCallback(() => {
    if (!selectedItemId) {
      if (orderedIds.length > 0) setSelectedItemForTriage(orderedIds[0]);
      return;
    }
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx < orderedIds.length - 1) setSelectedItemForTriage(orderedIds[idx + 1]);
  }, [selectedItemId, orderedIds, setSelectedItemForTriage]);

  const handleNavigatePrev = useCallback(() => {
    if (!selectedItemId) return;
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx > 0) setSelectedItemForTriage(orderedIds[idx - 1]);
    else setSelectedItemForTriage(null);
  }, [selectedItemId, orderedIds, setSelectedItemForTriage]);

  const handleJumpToEntity = useCallback(
    (entityId: string, dimension: string) => {
      setSelectedDimension(dimension as TriageDimension);
      setSelectedItemForTriage(entityId, "push");
    },
    [setSelectedItemForTriage]
  );

  // --- Publish flow ---
  const handleOpenPublishSheet = useCallback(() => {
    if (!permissions.canPublish) return;
    if (queuedPublishItems.length === 0) return;
    setPublishStatus("idle");
    setLastPublishRecord(null);
    setLastPublishedFallbackItems([]);
    handleCloseVersionHistory();
    setIsPublishSheetOpen(true);
  }, [handleCloseVersionHistory, permissions.canPublish, queuedPublishItems.length]);

  const handleClosePublishSheet = useCallback(() => {
    if (publishStatus === "publishing") return;
    setIsPublishSheetOpen(false);
  }, [publishStatus]);

  /** Closes the success state and returns the review page to its normal view. */
  const handleDonePublish = useCallback(() => {
    setIsPublishSheetOpen(false);
    setPublishStatus("idle");
    setLastPublishRecord(null);
    setLastPublishedFallbackItems([]);
  }, []);

  const handleOpenVersionHistory = useCallback(() => {
    setIsPublishSheetOpen(false);
    openVersionHistory();
  }, [openVersionHistory]);

  const markItemsAsPublished = useCallback((itemsToMark: QueuedPublishItem[]) => {
    const publishedIds = new Set(itemsToMark.map((item) => item.id));
    const publishedPendingDeleteModuleIds = new Set(
      itemsToMark
        .filter((item) => item.pendingDelete === true && item.entityType === ModernizedActivityEntityType.Module)
        .map((item) => item.id)
    );
    const publishedPendingDeleteActivityIds = new Set(
      itemsToMark
        .filter((item) => item.pendingDelete === true && item.entityType !== ModernizedActivityEntityType.Module)
        .map((item) => item.id)
    );
    setDecisions((prev) => {
      const next = new Map(prev);
      itemsToMark.forEach((item) => {
        if (publishedIds.has(item.id)) {
          next.set(item.id, { decision: "synced" });
        }
      });
      return next;
    });
    setLastPublishedFallbackItems(
      itemsToMark.map((item) => ({
        id: item.id,
        title: item.title,
        category: item.category,
        moduleName: item.moduleName,
      }))
    );
    setLocallyPendingDeleteModuleIds((current) => {
      if (publishedPendingDeleteModuleIds.size === 0) return current;
      const next = new Set(current);
      publishedPendingDeleteModuleIds.forEach((id) => next.delete(id));
      return next;
    });
    setLocallyPendingDeleteActivityIds((current) => {
      if (publishedPendingDeleteActivityIds.size === 0) return current;
      const next = new Set(current);
      publishedPendingDeleteActivityIds.forEach((id) => next.delete(id));
      return next;
    });
    setLocallyPublishedDeleteModuleIds((current) => {
      if (publishedPendingDeleteModuleIds.size === 0) return current;
      const next = new Set(current);
      publishedPendingDeleteModuleIds.forEach((id) => next.add(id));
      return next;
    });
    setLocallyPublishedDeleteActivityIds((current) => {
      if (publishedPendingDeleteActivityIds.size === 0) return current;
      const next = new Set(current);
      publishedPendingDeleteActivityIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const finishPublish = useCallback((publishRecord: PublishHistoryRecord | null) => {
    setLastPublishRecord(publishRecord);
    setPublishStatus("done");
  }, []);

  const lastPublishedItems = useMemo<PublishedPublishItem[]>(
    () => lastPublishRecord?.autoPublishedItems.map(mapPublishHistoryItem) ?? lastPublishedFallbackItems,
    [lastPublishedFallbackItems, lastPublishRecord]
  );

  const lastPublishManualSteps = useMemo<PublishManualStep[]>(
    () => lastPublishRecord?.manualSteps.map(mapPublishHistoryManualStep) ?? [],
    [lastPublishRecord]
  );

  const lastPublishDocumentUpdates = useMemo<PublishDocumentUpdate[]>(
    () => (lastPublishRecord ? review.documentUpdates : []),
    [lastPublishRecord, review.documentUpdates]
  );

  const hasLiveItems = useMemo(
    () => queuedPublishItems.some((item) => item.externalVisibility === ExternalVisibility.Published),
    [queuedPublishItems]
  );

  const executePublish = useCallback(async () => {
    if (!permissions.canPublish) return;
    if (queuedPublishItems.length === 0 || !review.canPublishToLms) return;
    const snapshot = [...queuedPublishItems];
    setPublishStatus("publishing");
    try {
      const mutationResult = await publishModernizedActivitiesMutation({
        variables: {
          courseId: review.courseId,
          itemIds: snapshot.map((item) => item.id),
        },
        update: (cache, result) => {
          if (result.data?.publishModernizedActivities.success !== true) {
            return;
          }

          snapshot.forEach((item) => updateActivityStatusInCache(cache, item, ModernizedActivityStatus.Published));
        },
        refetchQueries: [
          {
            query: GetCourseVersionHistoryDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      });
      if (!isMountedRef.current) return;
      const publishResult = mutationResult.data?.publishModernizedActivities;
      if (!publishResult?.success || !publishResult.publishEventId) {
        throw new Error("publishModernizedActivities mutation failed");
      }

      const publishedIds = new Set(publishResult.items.map((item) => item.id));
      if (publishedIds.size !== snapshot.length) {
        throw new Error("publishModernizedActivities returned partial item results");
      }

      const publishedSnapshot = snapshot.filter((item) => publishedIds.has(item.id));
      markItemsAsPublished(publishedSnapshot);

      let publishRecord: PublishHistoryRecord | null = null;
      try {
        const historyResult = await fetchPublishHistory({ variables: { courseId: review.courseId } });
        if (!isMountedRef.current) return;
        const publishHistoryResult = historyResult.data?.getModernizedCoursePublishHistory;
        publishRecord =
          publishHistoryResult?.success === true
            ? (publishHistoryResult.records.find((record) => record.id === publishResult.publishEventId) ?? null)
            : null;
      } catch {
        publishRecord = null;
      }

      if (!isMountedRef.current) return;
      if (!publishRecord) {
        finishPublish(null);
        return;
      }

      finishPublish(publishRecord);
    } catch {
      if (!isMountedRef.current) return;
      setPublishStatus("error");
    }
  }, [
    finishPublish,
    markItemsAsPublished,
    publishModernizedActivitiesMutation,
    queuedPublishItems,
    fetchPublishHistory,
    permissions.canPublish,
    review.canPublishToLms,
    review.courseId,
  ]);

  const handlePublish = useCallback(() => {
    if (!permissions.canPublish) return;
    if (hasLiveItems) {
      setIsLivePublishWarningVisible(true);
      return;
    }
    void executePublish();
  }, [hasLiveItems, executePublish, permissions.canPublish]);

  const handleConfirmLivePublish = useCallback(() => {
    if (!permissions.canPublish) return;
    setIsLivePublishWarningVisible(false);
    void executePublish();
  }, [executePublish, permissions.canPublish]);

  const handleCancelLivePublish = useCallback(() => {
    setIsLivePublishWarningVisible(false);
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!permissions.canPublish) return;

    setIsExportingPdf(true);
    try {
      const result = await requestModernizedCourseExportMutation({
        variables: {
          courseId: review.courseId,
          format: ModernizedCourseExportFormat.Pdf,
        },
      });
      const exportResult = result.data?.requestModernizedCourseExport;
      if (!exportResult?.success || !exportResult.downloadUrl) {
        throw new Error(exportResult?.message ?? "Failed to generate PDF export");
      }

      if (typeof window !== "undefined") {
        const managedFileAction = getManagedFileDownloadAction(exportResult.downloadUrl);
        if (managedFileAction) {
          await submitAuthenticatedHiddenDownloadForm(managedFileAction, `modernized-course-pdf-export-${Date.now()}`, {
            disposition: "attachment",
          });
        } else {
          window.open(exportResult.downloadUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to generate PDF export",
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setIsExportingPdf(false);
      }
    }
  }, [permissions.canPublish, requestModernizedCourseExportMutation, review.courseId, showToast]);

  const handleExportImscc = useCallback(async () => {
    if (!permissions.canPublish) return;

    setIsExportingImscc(true);
    try {
      const result = await requestModernizedCourseExportMutation({
        variables: {
          courseId: review.courseId,
          format: ModernizedCourseExportFormat.Imscc,
        },
      });
      const exportResult = result.data?.requestModernizedCourseExport;
      if (!exportResult?.success || !exportResult.downloadUrl) {
        throw new Error(exportResult?.message ?? "Failed to generate Common Cartridge export");
      }

      if (typeof window !== "undefined") {
        const managedFileAction = getManagedFileDownloadAction(exportResult.downloadUrl);
        if (managedFileAction) {
          await submitAuthenticatedHiddenDownloadForm(
            managedFileAction,
            `modernized-course-imscc-export-${Date.now()}`,
            {
              disposition: "attachment",
            }
          );
        } else {
          window.open(exportResult.downloadUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to generate Common Cartridge export",
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setIsExportingImscc(false);
      }
    }
  }, [permissions.canPublish, requestModernizedCourseExportMutation, review.courseId, showToast]);

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleEditingTeamChange = useCallback(
    async (nextMembers: Array<{ userId: string; role: EditingTeamRole }>) => {
      if (!permissions.canManageTeam) return;

      if (!teamManagement) {
        showToast({
          message: "Team management is not available for this course yet.",
          type: "error",
        });
        return;
      }

      const currentMembersById = new Map(teamManagement.editingTeam.map((member) => [member.user.id, member.role]));
      const nextMembersById = new Map(nextMembers.map((member) => [member.userId, member.role]));
      const operations: EditingTeamMutationOperation[] = [];

      currentMembersById.forEach((_, userId) => {
        if (!nextMembersById.has(userId)) {
          operations.push({ kind: "remove", userId });
        }
      });

      nextMembersById.forEach((role, userId) => {
        if (currentMembersById.get(userId) !== role) {
          operations.push({ kind: "set", userId, role });
        }
      });

      if (operations.length === 0) {
        return;
      }

      try {
        for (let index = 0; index < operations.length; index += 1) {
          const operation = operations[index];
          const shouldRefetch = index === operations.length - 1;
          const refetchOptions = shouldRefetch
            ? {
                refetchQueries: [{ query: GetModernizedCourseDocument, variables: { courseId: review.courseId } }],
                awaitRefetchQueries: true,
              }
            : {};

          if (operation.kind === "remove") {
            const result = await removeEditingTeamMemberMutation({
              variables: {
                courseId: review.courseId,
                userId: operation.userId,
              },
              ...refetchOptions,
            });
            const payload = result.data?.removeEditingTeamMember;
            if (!payload?.success) {
              throw new Error(payload?.message || "Failed to update the team");
            }
          } else {
            const result = await setEditingTeamMemberMutation({
              variables: {
                courseId: review.courseId,
                userId: operation.userId,
                role: operation.role,
              },
              ...refetchOptions,
            });
            const payload = result.data?.setEditingTeamMember;
            if (!payload?.success) {
              throw new Error(payload?.message || "Failed to update the team");
            }
          }
        }

        await refetchEditingTeam();
        showToast({ message: "Team updated", type: "success" });
      } catch (error) {
        showToast({
          message: error instanceof Error ? error.message : "Failed to update the team",
          type: "error",
        });
      }
    },
    [
      permissions.canManageTeam,
      refetchEditingTeam,
      removeEditingTeamMemberMutation,
      review.courseId,
      setEditingTeamMemberMutation,
      showToast,
      teamManagement,
    ]
  );

  return (
    <>
      <ReviewPanel
        review={displayReview}
        activityDetails={displayActivityDetails}
        permissions={permissions}
        editingTeamResult={editingTeamResult ?? null}
        currentUserId={currentUserId}
        teamManagement={displayReview.teamManagement}
        teamUserOptions={teamUserOptions}
        teamSearchQuery={teamSearchQuery}
        onTeamSearchQueryChange={setTeamSearchQuery}
        selectedTeamRole={selectedTeamRole}
        onSelectedTeamRoleChange={setSelectedTeamRole}
        isSavingTeamManagement={isSavingTeamManagement}
        onEditingTeamChange={handleEditingTeamChange}
        selectedItemId={selectedItemId}
        selectedReviewItem={displaySelectedSidebarItem}
        selectedDecision={displaySelectedDecision}
        filterMode={filterMode}
        collapsedSectionIds={collapsedSectionIds}
        reviewedProgress={displayReviewedProgress}
        getDecision={displayGetDecision}
        lastSavedAt={lastSavedAt}
        isSaving={isSaving || isFetchingCourseSnapshotReview}
        onExitReview={onExitReview}
        onSelectItem={setSelectedItemForTriage}
        onToggleSection={toggleSection}
        onFilterModeChange={setFilterMode}
        onStartReviewing={() => {
          // Select first undecided item
          const pendingDeleteIds = new Set(
            allItems.filter((item) => item.pendingDelete === true).map((item) => item.id)
          );
          const firstUndecided = orderedIds.find((id) => !pendingDeleteIds.has(id) && !decisions.get(id)?.decision);
          if (firstUndecided) setSelectedItemForTriage(firstUndecided);
        }}
        onAccept={handleAccept}
        onReject={handleReject}
        onAcceptGroup={handleAcceptGroup}
        onRejectGroup={handleRejectGroup}
        onModuleDragEnd={handleModuleDragEnd}
        onActivityDragEnd={handleActivityDragEnd}
        onActivityMoveEnd={handleActivityMoveEnd}
        onUndo={permissions.canQueue && undoEntry ? handleUndo : undefined}
        onSaveEdit={handleSaveEdit}
        onAddModule={handleOpenAddModule}
        onDeleteActivity={handleDeleteActivity}
        onUndoDeleteActivity={permissions.canRestoreVersion ? handleUndoDeleteActivity : undefined}
        onDeleteModule={handleDeleteModule}
        onUndoDeleteModule={permissions.canRestoreVersion ? handleUndoDeleteModule : undefined}
        onAddActivity={handleOpenAddActivity}
        onCreateIle={handleOpenIleCreation}
        onPreviewIle={handlePreviewIle}
        isGeneratingIle={Boolean(ileGenerationItemId) || isGeneratingIleRecommendationContent}
        onNavigateNext={handleNavigateNext}
        onNavigatePrev={handleNavigatePrev}
        onJumpToEntity={handleJumpToEntity}
        canAcceptSelectedItem={canAcceptSelectedItem}
        acceptDisabledReason={acceptDisabledReason}
        acceptHiddenReason={acceptHiddenReason}
        queuedPublishItems={queuedPublishItems}
        lastPublishedItems={lastPublishedItems}
        publishManualSteps={lastPublishManualSteps}
        publishDocumentUpdates={lastPublishDocumentUpdates}
        undecidedCount={stats.total - stats.decided}
        isPublishSheetOpen={isPublishSheetOpen}
        publishStatus={publishStatus}
        onOpenPublishSheet={handleOpenPublishSheet}
        onClosePublishSheet={handleClosePublishSheet}
        onDonePublish={handleDonePublish}
        onPublish={handlePublish}
        onExportPdf={handleExportPdf}
        onExportImscc={handleExportImscc}
        isExportingPdf={isExportingPdf}
        isExportingImscc={isExportingImscc}
        isVersionHistoryOpen={isVersionHistoryOpen}
        onOpenVersionHistory={handleOpenVersionHistory}
        onCloseVersionHistory={handleCloseVersionHistory}
        viewingSnapshot={viewingSnapshot}
        isRestoreSnapshotModalOpen={isRestoreSnapshotModalOpen}
        isRestoreSnapshotPending={isRestoreSnapshotPending}
        onViewSnapshot={handleViewSnapshot}
        onBackToCurrentSnapshot={handleBackToCurrentSnapshot}
        onOpenRestoreSnapshotConfirmation={handleOpenRestoreSnapshotConfirmation}
        onCancelRestoreSnapshot={handleCancelRestoreSnapshot}
        onConfirmRestoreSnapshot={handleConfirmRestoreSnapshot}
      />
      <ModernizerIleCreationSheet
        open={permissions.canEdit && Boolean(ileCreationItemId)}
        initialValues={ileCreationInitialValues}
        isSubmitting={isCreatingIle || isUpdatingIle}
        onSave={handleSaveIleCreation}
        onClose={handleCloseIleCreation}
      />
      <ModernizerAddAssignmentSheet
        open={permissions.canEdit && Boolean(assignmentModule)}
        moduleTitle={assignmentModule?.title ?? ""}
        isSubmitting={isCreatingModernizedAssignment}
        onSave={handleSaveModernizedAssignment}
        onClose={handleCloseAddAssignment}
      />
      <ModernizerAddModuleModal
        open={permissions.canEdit && isAddModuleModalOpen}
        isSubmitting={isCreatingModernizedModule}
        onSave={handleSaveModernizedModule}
        onClose={handleCloseAddModule}
      />
      <ModernizerAddActivityDrawer
        open={permissions.canEdit && Boolean(addActivityModule)}
        moduleTitle={addActivityModule?.title ?? ""}
        isSubmitting={isCreatingModernizedActivity}
        onAddAssignment={handleOpenAddAssignment}
        onSave={handleSaveModernizedActivity}
        onClose={handleCloseAddActivity}
      />
      <CustomAdminModal
        visible={Boolean(pendingModuleDelete)}
        onRequestClose={handleCloseModuleDeleteModal}
        title={
          pendingModuleDelete?.mode === "blocked"
            ? `Cannot Delete ${pendingModuleDelete.moduleItem.title}`
            : `Removing ${pendingModuleDelete?.moduleItem.title ?? "Module"}`
        }
        ariaLabel="Delete module">
        <View style={styles.warningModalContent}>
          <CustomText
            text={
              pendingModuleDelete?.mode === "blocked"
                ? pendingModuleDelete.stagedActivityCount > 0
                  ? pendingModuleDelete.stagedActivityCount === 1
                    ? "New modules can only be deleted when they have no activities. Delete the activity in this module first."
                    : `New modules can only be deleted when they have no activities. Delete the ${pendingModuleDelete.stagedActivityCount} activities in this module first.`
                  : pendingModuleDelete.activeActivityCount === 1
                    ? "This module has 1 active activity. Remove or skip the activity before deleting the module."
                    : `This module has ${pendingModuleDelete?.activeActivityCount ?? 0} active activities. Remove or skip the activities before deleting the module.`
                : "This module has no active activities. Existing LMS-backed modules will stay visible as pending delete until you publish; newly staged empty modules will be discarded."
            }
            useTranslationText={false}
            size="s"
            style={styles.warningModalText}
          />
          <View style={styles.warningModalButtons}>
            <Pressable
              onPress={handleCloseModuleDeleteModal}
              style={styles.warningModalCancelButton}
              accessibilityRole="button">
              <CustomText
                text={pendingModuleDelete?.mode === "blocked" ? "Close" : "Cancel"}
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalCancelText}
              />
            </Pressable>
            {pendingModuleDelete?.mode === "confirm" ? (
              <Pressable
                onPress={handleConfirmDeleteModule}
                disabled={isDeletingModernizedModule}
                style={[styles.warningModalConfirmButton, styles.deleteModuleConfirmButton]}
                accessibilityRole="button">
                <CustomText
                  text={isDeletingModernizedModule ? "Removing..." : "Remove module"}
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  style={styles.warningModalConfirmText}
                />
              </Pressable>
            ) : null}
          </View>
        </View>
      </CustomAdminModal>
      <CustomAdminModal
        visible={Boolean(pendingActivityDelete)}
        onRequestClose={handleCloseActivityDeleteModal}
        title={`Delete ${pendingActivityDelete?.item.title ?? "activity"}`}
        ariaLabel="Delete activity">
        <View style={styles.warningModalContent}>
          <CustomText
            text="Existing LMS-backed activities will stay visible as pending delete and will be deleted from the LMS when you publish. Newly staged activities will be discarded now and will not create a future LMS delete."
            useTranslationText={false}
            size="s"
            style={styles.warningModalText}
          />
          <View style={styles.warningModalButtons}>
            <Pressable
              onPress={handleCloseActivityDeleteModal}
              style={styles.warningModalCancelButton}
              accessibilityRole="button">
              <CustomText
                text="Cancel"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalCancelText}
              />
            </Pressable>
            <Pressable
              onPress={handleConfirmDeleteActivity}
              disabled={isDeletingModernizedActivity}
              style={[styles.warningModalConfirmButton, styles.deleteModuleConfirmButton]}
              accessibilityRole="button">
              <CustomText
                text={isDeletingModernizedActivity ? "Deleting..." : "Delete activity"}
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalConfirmText}
              />
            </Pressable>
          </View>
        </View>
      </CustomAdminModal>
      <CustomAdminModal
        visible={isLivePublishWarningVisible}
        onRequestClose={handleCancelLivePublish}
        title="Publishing to a Live Course"
        ariaLabel="Live Course Publish Warning">
        <View style={styles.warningModalContent}>
          <CustomText
            text={`This course is currently published and visible to enrolled students in ${review.sourceLabel}. Any changes you publish will go live immediately and students will see them right away.`}
            useTranslationText={false}
            size="s"
            style={styles.warningModalText}
          />
          <View style={styles.warningModalButtons}>
            <Pressable
              onPress={handleCancelLivePublish}
              style={styles.warningModalCancelButton}
              accessibilityRole="button">
              <CustomText
                text="Back"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalCancelText}
              />
            </Pressable>
            <Pressable
              onPress={handleConfirmLivePublish}
              style={styles.warningModalConfirmButton}
              accessibilityRole="button">
              <CustomText
                text="Publish Anyway"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalConfirmText}
              />
            </Pressable>
          </View>
        </View>
      </CustomAdminModal>
    </>
  );
}

const styles = StyleSheet.create({
  warningModalContent: {
    padding: 24,
    gap: 24,
  },
  warningModalText: {
    color: "#344054",
    lineHeight: 22,
  },
  warningModalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  warningModalCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
  },
  warningModalCancelText: {
    color: "#344054",
  },
  warningModalConfirmButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: "#B42318",
  },
  deleteModuleConfirmButton: {
    backgroundColor: "#D92D20",
  },
  warningModalConfirmText: {
    color: "#FFFFFF",
  },
});

export default CourseEvaluatorReviewContainer;
